import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import {
  Plus,
  Trash2,
  Wallet,
  ArrowDownCircle,
  ArrowUpCircle,
  Building,
  TrendingUp,
  TrendingDown,
  CreditCard,
  Search,
  Truck,
  RefreshCw,
  BookOpen,
  AlertCircle,
  Users,
  DollarSign,
  Clock,
  CheckCircle2,
  Download,
  Calendar,
  BarChart3,
  Filter,
  FileText,
  PieChart,
  Mail,
  TrendingUp as TrendingUpIcon,
  Users2,
  Percent,
  Save,
  Pencil,
  Send,
  Copy,
  CheckCheck,
  Building2,
  Phone,
  ExternalLink
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
  LineChart,
  Line
} from 'recharts';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AccountingPage = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [summary, setSummary] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [clients, setClients] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Client Ledger states
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [pendingUtrEntries, setPendingUtrEntries] = useState([]);
  const [outstandingData, setOutstandingData] = useState({ total_outstanding: 0, total_pending_utr: 0, clients: [] });
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [utrDialogOpen, setUtrDialogOpen] = useState(false);
  const [selectedLedgerEntry, setSelectedLedgerEntry] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    client_id: "",
    amount: "",
    payment_type: "balance",
    payment_mode: "bank_transfer",
    bank_account_id: "",
    utr_number: "",
    bank_name: "",
    description: "",
    tds_applicable: false,
    tds_rate: ""
  });
  const [utrForm, setUtrForm] = useState({
    utr_number: "",
    payment_mode: "bank_transfer",
    bank_name: ""
  });
  
  // Vendor Ledger states
  const [vendorLedgerEntries, setVendorLedgerEntries] = useState([]);
  const [vendorOutstandingData, setVendorOutstandingData] = useState({ total_outstanding: 0, total_pending_payments: 0, vendors: [] });
  const [vendorPaymentDialogOpen, setVendorPaymentDialogOpen] = useState(false);
  const [vendorUtrDialogOpen, setVendorUtrDialogOpen] = useState(false);
  const [selectedVendorEntry, setSelectedVendorEntry] = useState(null);
  const [vendorPaymentForm, setVendorPaymentForm] = useState({
    vendor_id: "",
    amount: "",
    payment_mode: "bank_transfer",
    bank_account_id: "",
    utr_number: "",
    description: "",
    vehicle_number: "",
    tds_applicable: false,
    tds_rate: "",
    // Vendor financial summary (for display in dialog)
    total_freight: 0,
    total_advance: 0,
    outstanding: 0
  });
  
  // TDS Report states
  const [tdsReportData, setTdsReportData] = useState({
    summary: { total_tds_receivable: 0, total_tds_payable: 0, net_tds: 0 },
    client_tds_entries: [],
    vendor_tds_entries: [],
    total_entries: 0
  });
  const [tdsDateFilter, setTdsDateFilter] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [tdsEmailDialogOpen, setTdsEmailDialogOpen] = useState(false);
  const [tdsEmailAddress, setTdsEmailAddress] = useState("");
  const [sendingTdsEmail, setSendingTdsEmail] = useState(false);
  
  // Bank Transfer Queue states
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [markTransferDialogOpen, setMarkTransferDialogOpen] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [transferForm, setTransferForm] = useState({
    utr_number: "",
    transfer_date: new Date().toISOString().slice(0, 10),
    remarks: ""
  });
  const [copiedField, setCopiedField] = useState(null);
  
  // Dialog states
  const [bankDialogOpen, setBankDialogOpen] = useState(false);
  const [transactionDialogOpen, setTransactionDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteType, setDeleteType] = useState(null);
  
  // Filter states
  const [transactionFilter, setTransactionFilter] = useState({
    type: "all",
    category: "all",
    bankAccountId: "all"
  });
  
  // Form states
  const [bankForm, setBankForm] = useState({
    account_name: "",
    bank_name: "",
    account_number: "",
    ifsc_code: "",
    branch: "",
    account_type: "current",
    opening_balance: 0
  });
  
  const [transactionForm, setTransactionForm] = useState({
    transaction_type: "receipt",
    category: "client_advance",
    amount: "",
    bank_account_id: "",
    reference_type: "client",
    reference_id: "",
    vehicle_number: "",
    description: "",
    transaction_date: new Date().toISOString().split('T')[0],
    payment_mode: "bank_transfer"
  });

  // Edit transaction state
  const [editTransactionDialogOpen, setEditTransactionDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [editTransactionForm, setEditTransactionForm] = useState({
    transaction_type: "",
    category: "",
    amount: "",
    bank_account_id: "",
    reference_type: "",
    reference_id: "",
    vehicle_number: "",
    description: "",
    transaction_date: "",
    payment_mode: ""
  });

  // Profit & Loss states
  const [orders, setOrders] = useState([]);
  const [profitLossData, setProfitLossData] = useState({
    totalFreight: 0,
    totalVendorCost: 0,
    grossProfit: 0,
    expenses: 0,
    staffSalary: 0,
    officeExpense: 0,
    fuelExpense: 0,
    maintenanceExpense: 0,
    otherExpense: 0,
    netProfit: 0,
    profitMargin: 0,
    orderWisePL: []
  });
  
  // P&L Date Filter states
  const [plDateFilter, setPlDateFilter] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    period: 'custom' // 'today', 'week', 'month', 'quarter', 'year', 'custom'
  });
  const [plComparison, setPlComparison] = useState({
    enabled: false,
    data: []
  });
  
  // Quarterly Report & Chart states
  const [quarterlyData, setQuarterlyData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [showChart, setShowChart] = useState(false);
  const [chartType, setChartType] = useState('area'); // 'area', 'bar', 'line'
  
  // Year-over-Year & Client Analysis states
  const [yoyData, setYoyData] = useState([]);
  const [clientProfitability, setClientProfitability] = useState([]);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  // Fetch pending transfers from vendor ledger (defined early for useEffect)
  const fetchPendingTransfers = useCallback(async () => {
    try {
      // Get vendor outstanding with pending payments
      const outstandingRes = await axios.get(`${API_URL}/vendor-ledger/outstanding`);
      const vendorData = outstandingRes.data.vendors || [];
      
      // Get vendor details for bank info
      const vendorsRes = await axios.get(`${API_URL}/vendors`);
      const vendorDetails = vendorsRes.data;
      
      // Build pending transfers list
      const transfers = vendorData
        .filter(v => v.outstanding > 0)
        .map(v => {
          const vendorInfo = vendorDetails.find(vd => vd.id === v.vendor_id) || {};
          const primaryBank = vendorInfo.bank_accounts?.[0] || {};
          
          return {
            vendor_id: v.vendor_id,
            vendor_name: v.vendor_name,
            total_freight: v.total_payable || 0,
            advance_paid: v.total_advance_paid || 0,
            balance_paid: v.total_balance_paid || 0,
            tds_deducted: v.total_tds_deducted || 0,
            outstanding: v.outstanding || 0,
            // Bank details
            account_number: primaryBank.account_number || "",
            ifsc_code: primaryBank.ifsc_code || "",
            bank_name: primaryBank.bank_name || "",
            account_holder: primaryBank.account_holder_name || vendorInfo.name || v.vendor_name,
            phone: vendorInfo.phone || "",
            // Status
            status: "pending"
          };
        });
      
      setPendingTransfers(transfers);
    } catch (error) {
      console.error("Error fetching pending transfers:", error);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
    fetchPendingTransfers();
  }, [fetchPendingTransfers]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchSummary(),
        fetchBankAccounts(),
        fetchTransactions(),
        fetchClients(),
        fetchVendors(),
        fetchLedgerData(),
        fetchVendorLedgerData(),
        fetchProfitLossData(),
        fetchTdsReport(tdsDateFilter.startDate, tdsDateFilter.endDate)
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfitLossData = async (startDate = null, endDate = null) => {
    try {
      const [ordersRes, transactionsRes] = await Promise.all([
        axios.get(`${API_URL}/orders`),
        axios.get(`${API_URL}/transactions`)
      ]);
      
      const ordersData = ordersRes.data;
      setOrders(ordersData);
      
      // Filter by date if provided
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate + 'T23:59:59') : null;
      
      // Calculate profit/loss for completed/assigned orders
      let filteredOrders = ordersData.filter(o => 
        o.status === 'completed' || o.status === 'assigned' || o.status === 'in_progress'
      );
      
      if (start && end) {
        filteredOrders = filteredOrders.filter(o => {
          const orderDate = new Date(o.created_at);
          return orderDate >= start && orderDate <= end;
        });
      }
      
      let totalFreight = 0;
      let totalVendorCost = 0;
      const orderWisePL = [];
      
      filteredOrders.forEach(order => {
        const freightAmount = parseFloat(order.freight_amount) || 0;  // Client freight (Income)
        const vendorCost = parseFloat(order.vendor_rate) || 0;  // Vendor rate (Cost)
        const profit = freightAmount - vendorCost;
        
        totalFreight += freightAmount;
        totalVendorCost += vendorCost;
        
        if (freightAmount > 0 || vendorCost > 0) {
          orderWisePL.push({
            id: order.id,
            order_number: order.order_number,
            client_name: order.client_name,
            vendor_name: order.assigned_vendor_name || '-',
            freight_amount: freightAmount,
            vendor_cost: vendorCost,
            profit: profit,
            profit_margin: freightAmount > 0 ? ((profit / freightAmount) * 100).toFixed(1) : 0,
            status: order.status,
            date: order.created_at
          });
        }
      });
      
      // Calculate expenses by category from transactions
      const expenseTransactions = (transactionsRes.data || []).filter(t => t.category?.startsWith('expense_'));
      
      // Filter by date if needed
      let filteredExpenses = expenseTransactions;
      if (start && end) {
        filteredExpenses = expenseTransactions.filter(t => {
          const transDate = new Date(t.transaction_date);
          return transDate >= start && transDate <= end;
        });
      }
      
      // Calculate categorized expenses
      let staffSalary = 0;
      let officeExpense = 0;
      let fuelExpense = 0;
      let maintenanceExpense = 0;
      let otherExpense = 0;
      
      filteredExpenses.forEach(exp => {
        const amount = parseFloat(exp.amount) || 0;
        switch(exp.category) {
          case 'expense_salary':
            staffSalary += amount;
            break;
          case 'expense_office':
          case 'expense_stationery':
            officeExpense += amount;
            break;
          case 'expense_fuel':
            fuelExpense += amount;
            break;
          case 'expense_maintenance':
            maintenanceExpense += amount;
            break;
          default:
            otherExpense += amount;
        }
      });
      
      const totalExpenses = staffSalary + officeExpense + fuelExpense + maintenanceExpense + otherExpense;
      
      const grossProfit = totalFreight - totalVendorCost;
      const netProfit = grossProfit - totalExpenses;
      const profitMargin = totalFreight > 0 ? ((grossProfit / totalFreight) * 100).toFixed(1) : 0;
      
      setProfitLossData({
        totalFreight,
        totalVendorCost,
        grossProfit,
        expenses: totalExpenses,
        staffSalary,
        officeExpense,
        fuelExpense,
        maintenanceExpense,
        otherExpense,
        netProfit,
        profitMargin,
        orderWisePL: orderWisePL.sort((a, b) => new Date(b.date) - new Date(a.date))
      });
    } catch (error) {
      console.error("Error fetching profit/loss data:", error);
    }
  };

  // TDS Report Fetch Function
  const fetchTdsReport = async (startDate = null, endDate = null) => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const response = await axios.get(`${API_URL}/tds-report?${params.toString()}`);
      setTdsReportData(response.data);
    } catch (error) {
      console.error("Error fetching TDS report:", error);
    }
  };

  const handleTdsPeriodChange = (period) => {
    const today = new Date();
    let startDate, endDate;
    
    switch(period) {
      case 'month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case 'quarter':
        const quarterMonth = Math.floor(today.getMonth() / 3) * 3;
        startDate = new Date(today.getFullYear(), quarterMonth, 1).toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case 'financial_year':
        // Indian Financial Year: April to March
        const fyYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
        startDate = new Date(fyYear, 3, 1).toISOString().split('T')[0]; // April 1
        endDate = today.toISOString().split('T')[0];
        break;
      case 'last_month':
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        startDate = lastMonth.toISOString().split('T')[0];
        endDate = lastMonthEnd.toISOString().split('T')[0];
        break;
      case 'last_quarter':
        const lastQuarterMonth = Math.floor(today.getMonth() / 3) * 3 - 3;
        const lqYear = lastQuarterMonth < 0 ? today.getFullYear() - 1 : today.getFullYear();
        const lqMonth = lastQuarterMonth < 0 ? 12 + lastQuarterMonth : lastQuarterMonth;
        startDate = new Date(lqYear, lqMonth, 1).toISOString().split('T')[0];
        const lqEndMonth = lqMonth + 2;
        endDate = new Date(lqYear, lqEndMonth + 1, 0).toISOString().split('T')[0];
        break;
      default:
        return;
    }
    
    setTdsDateFilter({ ...tdsDateFilter, startDate, endDate });
    fetchTdsReport(startDate, endDate);
  };

  const applyTdsDateFilter = () => {
    fetchTdsReport(tdsDateFilter.startDate, tdsDateFilter.endDate);
  };

  // Export TDS Report to CSV
  const exportTdsToCSV = () => {
    const rows = [
      ['TDS REPORT'],
      [`Period: ${tdsDateFilter.startDate} to ${tdsDateFilter.endDate}`],
      [''],
      ['SUMMARY'],
      ['TDS Receivable (Deducted by Clients)', tdsReportData.summary.total_tds_receivable],
      ['TDS Payable (Deducted from Vendors)', tdsReportData.summary.total_tds_payable],
      ['Net TDS Liability', tdsReportData.summary.net_tds],
      [''],
      ['TDS DEDUCTED BY CLIENTS (Receivable)'],
      ['Date', 'Client', 'Entry No', 'Gross Amount', 'TDS Rate', 'TDS Amount', 'Net Amount'],
      ...tdsReportData.client_tds_entries.map(e => [
        e.transaction_date,
        e.client_name,
        e.entry_number,
        e.amount,
        `${e.tds_rate}%`,
        e.tds_amount,
        e.net_amount
      ]),
      [''],
      ['TDS DEDUCTED FROM VENDORS (Payable)'],
      ['Date', 'Vendor', 'Entry No', 'Gross Amount', 'TDS Rate', 'TDS Amount', 'Net Paid'],
      ...tdsReportData.vendor_tds_entries.map(e => [
        e.transaction_date,
        e.vendor_name,
        e.entry_number,
        e.amount,
        `${e.tds_rate}%`,
        e.tds_amount,
        e.net_amount
      ])
    ];
    
    const csvContent = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `TDS_Report_${tdsDateFilter.startDate}_to_${tdsDateFilter.endDate}.csv`;
    link.click();
    toast.success('TDS Report exported to CSV');
  };

  // Send TDS Report Email
  const sendTdsReportEmail = async () => {
    if (!tdsEmailAddress) {
      toast.error("Please enter an email address");
      return;
    }
    
    setSendingTdsEmail(true);
    try {
      const htmlContent = `
        <div style="font-family: Arial, sans-serif; max-width: 700px; margin: 0 auto;">
          <h2 style="color: #7c3aed;">TDS Report - ${tdsDateFilter.startDate} to ${tdsDateFilter.endDate}</h2>
          <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
          
          <h3 style="color: #059669; margin-top: 20px;">Summary</h3>
          <table style="border-collapse: collapse; width: 100%; max-width: 500px;">
            <tr style="background: #f3f4f6;">
              <td style="padding: 10px; border: 1px solid #ddd;">TDS Receivable (From Clients)</td>
              <td style="padding: 10px; border: 1px solid #ddd; text-align: right; color: #059669;">₹${tdsReportData.summary.total_tds_receivable.toLocaleString()}</td>
            </tr>
            <tr>
              <td style="padding: 10px; border: 1px solid #ddd;">TDS Payable (To Govt)</td>
              <td style="padding: 10px; border: 1px solid #ddd; text-align: right; color: #7c3aed;">₹${tdsReportData.summary.total_tds_payable.toLocaleString()}</td>
            </tr>
            <tr style="background: ${tdsReportData.summary.net_tds >= 0 ? '#dcfce7' : '#fee2e2'};">
              <td style="padding: 10px; border: 1px solid #ddd; font-weight: bold;">Net TDS ${tdsReportData.summary.net_tds >= 0 ? 'Payable' : 'Receivable'}</td>
              <td style="padding: 10px; border: 1px solid #ddd; text-align: right; font-weight: bold; color: ${tdsReportData.summary.net_tds >= 0 ? '#7c3aed' : '#059669'};">₹${Math.abs(tdsReportData.summary.net_tds).toLocaleString()}</td>
            </tr>
          </table>
          
          <h3 style="color: #059669; margin-top: 20px;">TDS Breakdown by Rate</h3>
          <table style="border-collapse: collapse; width: 100%; max-width: 500px;">
            <tr style="background: #f3f4f6;">
              <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Category</th>
              <th style="padding: 8px; border: 1px solid #ddd; text-align: right;">1%</th>
              <th style="padding: 8px; border: 1px solid #ddd; text-align: right;">2%</th>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd;">Client TDS</td>
              <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">₹${(tdsReportData.summary.client_tds_1_percent || 0).toLocaleString()}</td>
              <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">₹${(tdsReportData.summary.client_tds_2_percent || 0).toLocaleString()}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd;">Vendor TDS</td>
              <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">₹${(tdsReportData.summary.vendor_tds_1_percent || 0).toLocaleString()}</td>
              <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">₹${(tdsReportData.summary.vendor_tds_2_percent || 0).toLocaleString()}</td>
            </tr>
          </table>
          
          <p style="color: #6b7280; font-size: 12px; margin-top: 30px;">
            This is an automated TDS report from Ardhanageshwar Logistics TMS.<br/>
            Total Transactions: ${tdsReportData.total_entries}
          </p>
        </div>
      `;
      
      await axios.post(`${API_URL}/send-tds-report`, {
        to_email: tdsEmailAddress,
        subject: `TDS Report - ${tdsDateFilter.startDate} to ${tdsDateFilter.endDate}`,
        html_content: htmlContent,
        start_date: tdsDateFilter.startDate,
        end_date: tdsDateFilter.endDate
      });
      
      toast.success(`TDS Report sent to ${tdsEmailAddress}`);
      setTdsEmailDialogOpen(false);
      setTdsEmailAddress("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send TDS report");
    } finally {
      setSendingTdsEmail(false);
    }
  };

  // ============== BANK TRANSFER QUEUE FUNCTIONS ==============
  
  // Copy to clipboard helper
  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      toast.success(`${fieldName} copied to clipboard`);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      toast.error("Failed to copy");
    }
  };

  // Copy all bank details for a vendor
  const copyAllBankDetails = (transfer) => {
    const details = `
Beneficiary Name: ${transfer.account_holder}
Account Number: ${transfer.account_number}
IFSC Code: ${transfer.ifsc_code}
Bank Name: ${transfer.bank_name}
Amount: ₹${transfer.outstanding.toLocaleString()}
    `.trim();
    
    copyToClipboard(details, "Bank Details");
  };

  // Mark transfer as completed
  const markTransferCompleted = async () => {
    if (!selectedTransfer || !transferForm.utr_number) {
      toast.error("Please enter UTR/Reference number");
      return;
    }
    
    try {
      // Get bank account for payment
      const bankAccount = bankAccounts.find(b => b.is_primary) || bankAccounts[0];
      
      if (!bankAccount) {
        toast.error("Please add a bank account first");
        return;
      }
      
      // Create vendor payment
      const params = new URLSearchParams();
      params.append("vendor_id", selectedTransfer.vendor_id);
      params.append("amount", selectedTransfer.outstanding.toString());
      params.append("payment_mode", "bank_transfer");
      params.append("bank_account_id", bankAccount.id);
      params.append("utr_number", transferForm.utr_number);
      params.append("description", `Bank transfer - ${transferForm.remarks || 'UCO Bank'}`);
      
      await axios.post(`${API_URL}/vendor-ledger/make-payment?${params.toString()}`);
      
      toast.success(`Payment of ₹${selectedTransfer.outstanding.toLocaleString()} to ${selectedTransfer.vendor_name} marked as completed`);
      setMarkTransferDialogOpen(false);
      setSelectedTransfer(null);
      setTransferForm({ utr_number: "", transfer_date: new Date().toISOString().slice(0, 10), remarks: "" });
      
      // Refresh data
      fetchPendingTransfers();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record payment");
    }
  };

  // Export pending transfers to Excel/CSV
  const exportPendingTransfers = () => {
    const rows = [
      ['PENDING BANK TRANSFERS - UCO BANK'],
      [`Generated: ${new Date().toLocaleString()}`],
      [''],
      ['Vendor Name', 'Account Holder', 'Account Number', 'IFSC Code', 'Bank Name', 'Amount (₹)', 'Phone'],
      ...pendingTransfers.map(t => [
        t.vendor_name,
        t.account_holder,
        t.account_number,
        t.ifsc_code,
        t.bank_name,
        t.outstanding,
        t.phone
      ]),
      [''],
      ['TOTAL', '', '', '', '', pendingTransfers.reduce((sum, t) => sum + t.outstanding, 0), '']
    ];
    
    const csvContent = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Pending_Bank_Transfers_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    toast.success('Pending transfers exported to CSV');
  };

  // P&L Date Filter Handlers
  const handlePLPeriodChange = (period) => {
    const today = new Date();
    let startDate, endDate;
    
    switch(period) {
      case 'today':
        startDate = endDate = today.toISOString().split('T')[0];
        break;
      case 'week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        startDate = weekStart.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case 'month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case 'quarter':
        const quarterMonth = Math.floor(today.getMonth() / 3) * 3;
        startDate = new Date(today.getFullYear(), quarterMonth, 1).toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case 'year':
        startDate = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      default:
        return;
    }
    
    setPlDateFilter({ startDate, endDate, period });
    fetchProfitLossData(startDate, endDate);
  };

  const applyPLDateFilter = () => {
    fetchProfitLossData(plDateFilter.startDate, plDateFilter.endDate);
  };

  // Export P&L to CSV/Excel
  const exportPLToCSV = () => {
    const headers = ['Order No', 'Date', 'Client', 'Vendor', 'Freight (₹)', 'Vendor Cost (₹)', 'Profit (₹)', 'Margin %', 'Status'];
    const rows = profitLossData.orderWisePL.map(order => [
      order.order_number,
      new Date(order.date).toLocaleDateString(),
      order.client_name,
      order.vendor_name,
      order.freight_amount,
      order.vendor_cost,
      order.profit,
      order.profit_margin + '%',
      order.status
    ]);
    
    // Add summary rows
    rows.push([]);
    rows.push(['SUMMARY', '', '', '', '', '', '', '', '']);
    rows.push(['Total Freight (Income)', '', '', '', profitLossData.totalFreight, '', '', '', '']);
    rows.push(['Total Vendor Cost', '', '', '', '', profitLossData.totalVendorCost, '', '', '']);
    rows.push(['Gross Profit', '', '', '', '', '', profitLossData.grossProfit, profitLossData.profitMargin + '%', '']);
    rows.push([]);
    rows.push(['OPERATING EXPENSES', '', '', '', '', '', '', '', '']);
    rows.push(['Staff Salary', '', '', '', '', profitLossData.staffSalary, '', '', '']);
    rows.push(['Office & Stationery', '', '', '', '', profitLossData.officeExpense, '', '', '']);
    rows.push(['Fuel Expense', '', '', '', '', profitLossData.fuelExpense, '', '', '']);
    rows.push(['Maintenance', '', '', '', '', profitLossData.maintenanceExpense, '', '', '']);
    rows.push(['Other Expenses', '', '', '', '', profitLossData.otherExpense, '', '', '']);
    rows.push(['Total Operating Expenses', '', '', '', '', profitLossData.expenses, '', '', '']);
    rows.push([]);
    rows.push(['NET PROFIT/LOSS', '', '', '', '', '', profitLossData.netProfit, '', '']);
    
    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `ProfitLoss_${plDateFilter.startDate}_to_${plDateFilter.endDate}.csv`;
    link.click();
    toast.success('P&L Report exported to CSV');
  };

  // Monthly Comparison Data
  const getMonthlyComparison = async () => {
    const monthlyData = [];
    const today = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const monthDate = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
      const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
      
      const monthOrders = orders.filter(o => {
        const orderDate = new Date(o.created_at);
        return orderDate >= monthStart && orderDate <= monthEnd &&
          (o.status === 'completed' || o.status === 'assigned' || o.status === 'in_progress');
      });
      
      let freight = 0, vendorCost = 0;
      monthOrders.forEach(o => {
        freight += parseFloat(o.freight_amount) || 0;
        vendorCost += parseFloat(o.advance_amount) || 0;
      });
      
      monthlyData.push({
        month: monthDate.toLocaleString('default', { month: 'short', year: 'numeric' }),
        freight,
        vendorCost,
        profit: freight - vendorCost,
        orders: monthOrders.length
      });
    }
    
    setPlComparison({ enabled: true, data: monthlyData });
    setChartData(monthlyData);
    setShowChart(true);
  };

  // Quarterly P&L Report
  const getQuarterlyReport = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const quarters = [];
    
    // Get last 4 quarters
    for (let q = 3; q >= 0; q--) {
      const quarterNum = Math.floor(today.getMonth() / 3) - q;
      let year = currentYear;
      let actualQuarter = quarterNum;
      
      if (quarterNum < 0) {
        year = currentYear - 1;
        actualQuarter = 4 + quarterNum;
      }
      
      const quarterStart = new Date(year, actualQuarter * 3, 1);
      const quarterEnd = new Date(year, (actualQuarter + 1) * 3, 0);
      
      const quarterOrders = orders.filter(o => {
        const orderDate = new Date(o.created_at);
        return orderDate >= quarterStart && orderDate <= quarterEnd &&
          (o.status === 'completed' || o.status === 'assigned' || o.status === 'in_progress');
      });
      
      let freight = 0, vendorCost = 0;
      quarterOrders.forEach(o => {
        freight += parseFloat(o.freight_amount) || 0;
        vendorCost += parseFloat(o.advance_amount) || 0;
      });
      
      quarters.push({
        quarter: `Q${actualQuarter + 1} ${year}`,
        period: `${quarterStart.toLocaleDateString('en-IN', { month: 'short' })} - ${quarterEnd.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}`,
        freight,
        vendorCost,
        profit: freight - vendorCost,
        margin: freight > 0 ? ((freight - vendorCost) / freight * 100).toFixed(1) : 0,
        orders: quarterOrders.length
      });
    }
    
    setQuarterlyData(quarters);
  };

  // Export P&L to PDF
  const exportPLToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFontSize(20);
    doc.setTextColor(30, 64, 175);
    doc.text('PROFIT & LOSS STATEMENT', pageWidth / 2, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Period: ${plDateFilter.startDate} to ${plDateFilter.endDate}`, pageWidth / 2, 28, { align: 'center' });
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, 33, { align: 'center' });
    
    // Summary Section
    doc.setFontSize(14);
    doc.setTextColor(0);
    doc.text('SUMMARY', 14, 45);
    
    doc.autoTable({
      startY: 50,
      head: [['Description', 'Amount (₹)']],
      body: [
        ['Total Freight (Income)', profitLossData.totalFreight.toLocaleString()],
        ['Vendor Cost (Direct Expense)', `(${profitLossData.totalVendorCost.toLocaleString()})`],
        ['Gross Profit', profitLossData.grossProfit.toLocaleString()],
        ['', ''],
        ['OPERATING EXPENSES', ''],
        ['Staff Salary', `(${profitLossData.staffSalary.toLocaleString()})`],
        ['Office & Stationery', `(${profitLossData.officeExpense.toLocaleString()})`],
        ['Fuel Expense', `(${profitLossData.fuelExpense.toLocaleString()})`],
        ['Maintenance', `(${profitLossData.maintenanceExpense.toLocaleString()})`],
        ['Other Expenses', `(${profitLossData.otherExpense.toLocaleString()})`],
        ['Total Operating Expenses', `(${profitLossData.expenses.toLocaleString()})`],
        ['', ''],
        ['NET PROFIT/LOSS', profitLossData.netProfit.toLocaleString()],
      ],
      theme: 'grid',
      headStyles: { fillColor: [30, 64, 175] },
      footStyles: { fillColor: [240, 240, 240] },
      columnStyles: {
        0: { cellWidth: 100 },
        1: { cellWidth: 60, halign: 'right' }
      }
    });
    
    // Key Metrics
    const metricsY = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(14);
    doc.text('KEY METRICS', 14, metricsY);
    
    doc.autoTable({
      startY: metricsY + 5,
      head: [['Metric', 'Value']],
      body: [
        ['Total Orders', profitLossData.orderWisePL.length.toString()],
        ['Gross Profit Margin', `${profitLossData.profitMargin}%`],
        ['Average Freight per Order', profitLossData.orderWisePL.length > 0 
          ? `₹${(profitLossData.totalFreight / profitLossData.orderWisePL.length).toLocaleString(undefined, {maximumFractionDigits: 0})}` 
          : '₹0'],
        ['Average Profit per Order', profitLossData.orderWisePL.length > 0 
          ? `₹${(profitLossData.grossProfit / profitLossData.orderWisePL.length).toLocaleString(undefined, {maximumFractionDigits: 0})}` 
          : '₹0'],
      ],
      theme: 'striped',
      headStyles: { fillColor: [34, 197, 94] },
    });
    
    // Order Details (new page if needed)
    if (profitLossData.orderWisePL.length > 0) {
      doc.addPage();
      doc.setFontSize(14);
      doc.text('ORDER-WISE BREAKDOWN', 14, 20);
      
      doc.autoTable({
        startY: 25,
        head: [['Order No', 'Client', 'Vendor', 'Freight', 'Cost', 'Profit', 'Margin']],
        body: profitLossData.orderWisePL.map(o => [
          o.order_number,
          o.client_name?.substring(0, 15) || '-',
          o.vendor_name?.substring(0, 15) || '-',
          o.freight_amount.toLocaleString(),
          o.vendor_cost.toLocaleString(),
          o.profit.toLocaleString(),
          `${o.profit_margin}%`
        ]),
        theme: 'grid',
        headStyles: { fillColor: [30, 64, 175], fontSize: 8 },
        bodyStyles: { fontSize: 7 },
        columnStyles: {
          3: { halign: 'right' },
          4: { halign: 'right' },
          5: { halign: 'right' },
          6: { halign: 'right' }
        }
      });
    }
    
    // Quarterly Summary (if available)
    if (quarterlyData.length > 0) {
      const qY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 15 : 20;
      if (qY > 250) doc.addPage();
      
      doc.setFontSize(14);
      doc.text('QUARTERLY COMPARISON', 14, qY > 250 ? 20 : qY);
      
      doc.autoTable({
        startY: qY > 250 ? 25 : qY + 5,
        head: [['Quarter', 'Orders', 'Freight (₹)', 'Vendor Cost (₹)', 'Profit (₹)', 'Margin %']],
        body: quarterlyData.map(q => [
          q.quarter,
          q.orders,
          q.freight.toLocaleString(),
          q.vendorCost.toLocaleString(),
          q.profit.toLocaleString(),
          `${q.margin}%`
        ]),
        theme: 'grid',
        headStyles: { fillColor: [147, 51, 234] },
      });
    }
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(
        `Ardhanageshwar Logistics - P&L Report | Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }
    
    doc.save(`ProfitLoss_${plDateFilter.startDate}_to_${plDateFilter.endDate}.pdf`);
    toast.success('P&L Report exported to PDF');
  };

  // Year-over-Year Comparison
  const getYearOverYearComparison = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const years = [];
    
    // Compare last 3 years
    for (let i = 2; i >= 0; i--) {
      const year = currentYear - i;
      const yearStart = new Date(year, 0, 1);
      const yearEnd = new Date(year, 11, 31, 23, 59, 59);
      
      const yearOrders = orders.filter(o => {
        const orderDate = new Date(o.created_at);
        return orderDate >= yearStart && orderDate <= yearEnd &&
          (o.status === 'completed' || o.status === 'assigned' || o.status === 'in_progress');
      });
      
      let freight = 0, vendorCost = 0;
      yearOrders.forEach(o => {
        freight += parseFloat(o.freight_amount) || 0;
        vendorCost += parseFloat(o.advance_amount) || 0;
      });
      
      const profit = freight - vendorCost;
      const prevYear = years.find(y => y.year === year - 1);
      
      years.push({
        year,
        freight,
        vendorCost,
        profit,
        margin: freight > 0 ? ((profit / freight) * 100).toFixed(1) : 0,
        orders: yearOrders.length,
        freightGrowth: prevYear && prevYear.freight > 0 
          ? (((freight - prevYear.freight) / prevYear.freight) * 100).toFixed(1) 
          : null,
        profitGrowth: prevYear && prevYear.profit > 0 
          ? (((profit - prevYear.profit) / prevYear.profit) * 100).toFixed(1) 
          : null
      });
    }
    
    setYoyData(years);
  };

  // Client-wise Profitability Analysis
  const getClientProfitability = () => {
    const clientMap = {};
    
    orders.forEach(order => {
      if (!order.client_name) return;
      
      const clientName = order.client_name;
      const freight = parseFloat(order.freight_amount) || 0;
      const vendorCost = parseFloat(order.advance_amount) || 0;
      
      if (!clientMap[clientName]) {
        clientMap[clientName] = {
          client_name: clientName,
          totalFreight: 0,
          totalVendorCost: 0,
          totalProfit: 0,
          orderCount: 0
        };
      }
      
      clientMap[clientName].totalFreight += freight;
      clientMap[clientName].totalVendorCost += vendorCost;
      clientMap[clientName].totalProfit += (freight - vendorCost);
      clientMap[clientName].orderCount += 1;
    });
    
    const clientList = Object.values(clientMap)
      .map(c => ({
        ...c,
        avgFreightPerOrder: c.orderCount > 0 ? (c.totalFreight / c.orderCount).toFixed(0) : 0,
        avgProfitPerOrder: c.orderCount > 0 ? (c.totalProfit / c.orderCount).toFixed(0) : 0,
        margin: c.totalFreight > 0 ? ((c.totalProfit / c.totalFreight) * 100).toFixed(1) : 0
      }))
      .sort((a, b) => b.totalProfit - a.totalProfit);
    
    setClientProfitability(clientList);
  };

  // Email P&L Report
  const sendPLReportEmail = async () => {
    if (!emailAddress || !emailAddress.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setSendingEmail(true);
    
    try {
      // Build HTML email content
      const emailContent = `
        <h2 style="color: #1E40AF;">Profit & Loss Report</h2>
        <p><strong>Period:</strong> ${plDateFilter.startDate} to ${plDateFilter.endDate}</p>
        <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
        
        <h3 style="color: #059669;">Summary</h3>
        <table style="border-collapse: collapse; width: 100%; max-width: 500px;">
          <tr style="background: #f3f4f6;">
            <td style="padding: 10px; border: 1px solid #ddd;">Total Freight (Income)</td>
            <td style="padding: 10px; border: 1px solid #ddd; text-align: right; color: #059669;">₹${profitLossData.totalFreight.toLocaleString()}</td>
          </tr>
          <tr>
            <td style="padding: 10px; border: 1px solid #ddd;">Vendor Cost</td>
            <td style="padding: 10px; border: 1px solid #ddd; text-align: right; color: #dc2626;">₹${profitLossData.totalVendorCost.toLocaleString()}</td>
          </tr>
          <tr style="background: #dbeafe;">
            <td style="padding: 10px; border: 1px solid #ddd; font-weight: bold;">Gross Profit</td>
            <td style="padding: 10px; border: 1px solid #ddd; text-align: right; color: #3b82f6; font-weight: bold;">₹${profitLossData.grossProfit.toLocaleString()}</td>
          </tr>
        </table>
        
        <h3 style="color: #f97316; margin-top: 20px;">Operating Expenses</h3>
        <table style="border-collapse: collapse; width: 100%; max-width: 500px;">
          ${profitLossData.staffSalary > 0 ? `<tr><td style="padding: 8px; border: 1px solid #ddd;">Staff Salary</td><td style="padding: 8px; border: 1px solid #ddd; text-align: right;">₹${profitLossData.staffSalary.toLocaleString()}</td></tr>` : ''}
          ${profitLossData.officeExpense > 0 ? `<tr><td style="padding: 8px; border: 1px solid #ddd;">Office & Stationery</td><td style="padding: 8px; border: 1px solid #ddd; text-align: right;">₹${profitLossData.officeExpense.toLocaleString()}</td></tr>` : ''}
          ${profitLossData.fuelExpense > 0 ? `<tr><td style="padding: 8px; border: 1px solid #ddd;">Fuel Expense</td><td style="padding: 8px; border: 1px solid #ddd; text-align: right;">₹${profitLossData.fuelExpense.toLocaleString()}</td></tr>` : ''}
          ${profitLossData.maintenanceExpense > 0 ? `<tr><td style="padding: 8px; border: 1px solid #ddd;">Maintenance</td><td style="padding: 8px; border: 1px solid #ddd; text-align: right;">₹${profitLossData.maintenanceExpense.toLocaleString()}</td></tr>` : ''}
          ${profitLossData.otherExpense > 0 ? `<tr><td style="padding: 8px; border: 1px solid #ddd;">Other Expenses</td><td style="padding: 8px; border: 1px solid #ddd; text-align: right;">₹${profitLossData.otherExpense.toLocaleString()}</td></tr>` : ''}
          <tr style="background: #fff7ed;">
            <td style="padding: 10px; border: 1px solid #ddd; font-weight: bold;">Total Expenses</td>
            <td style="padding: 10px; border: 1px solid #ddd; text-align: right; color: #f97316; font-weight: bold;">₹${profitLossData.expenses.toLocaleString()}</td>
          </tr>
        </table>
        
        <table style="border-collapse: collapse; width: 100%; max-width: 500px; margin-top: 15px;">
          <tr style="background: ${profitLossData.netProfit >= 0 ? '#dcfce7' : '#fee2e2'};">
            <td style="padding: 12px; border: 2px solid ${profitLossData.netProfit >= 0 ? '#059669' : '#dc2626'}; font-weight: bold; font-size: 16px;">Net ${profitLossData.netProfit >= 0 ? 'Profit' : 'Loss'}</td>
            <td style="padding: 12px; border: 2px solid ${profitLossData.netProfit >= 0 ? '#059669' : '#dc2626'}; text-align: right; font-weight: bold; font-size: 16px; color: ${profitLossData.netProfit >= 0 ? '#059669' : '#dc2626'};">₹${Math.abs(profitLossData.netProfit).toLocaleString()}</td>
          </tr>
        </table>
        
        <h3 style="color: #6366f1; margin-top: 20px;">Key Metrics</h3>
        <ul>
          <li>Total Orders: ${profitLossData.orderWisePL.length}</li>
          <li>Profit Margin: ${profitLossData.profitMargin}%</li>
          <li>Avg Freight/Order: ₹${profitLossData.orderWisePL.length > 0 ? (profitLossData.totalFreight / profitLossData.orderWisePL.length).toLocaleString(undefined, {maximumFractionDigits: 0}) : 0}</li>
        </ul>
        
        <p style="color: #6b7280; font-size: 12px; margin-top: 30px;">
          This is an automated report from Ardhanageshwar Logistics TMS.<br/>
          For detailed analysis, please login to the dashboard.
        </p>
      `;
      
      await axios.post(`${API_URL}/send-pl-report`, {
        to_email: emailAddress,
        subject: `P&L Report: ${plDateFilter.startDate} to ${plDateFilter.endDate}`,
        html_content: emailContent
      });
      
      toast.success(`P&L Report sent to ${emailAddress}`);
      setEmailDialogOpen(false);
      setEmailAddress('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  const fetchLedgerData = async () => {
    try {
      const [ledgerRes, outstandingRes, pendingRes] = await Promise.all([
        axios.get(`${API_URL}/client-ledger`),
        axios.get(`${API_URL}/client-ledger/outstanding`),
        axios.get(`${API_URL}/client-ledger/pending-utr`)
      ]);
      setLedgerEntries(ledgerRes.data);
      setOutstandingData(outstandingRes.data);
      setPendingUtrEntries(pendingRes.data.entries || []);
    } catch (error) {
      console.error("Error fetching ledger data:", error);
    }
  };

  const fetchVendorLedgerData = async () => {
    try {
      const [ledgerRes, outstandingRes] = await Promise.all([
        axios.get(`${API_URL}/vendor-ledger`),
        axios.get(`${API_URL}/vendor-ledger/outstanding`)
      ]);
      setVendorLedgerEntries(ledgerRes.data);
      setVendorOutstandingData(outstandingRes.data);
    } catch (error) {
      console.error("Error fetching vendor ledger data:", error);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API_URL}/accounting/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error("Error fetching summary:", error);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const response = await axios.get(`${API_URL}/bank-accounts`);
      setBankAccounts(response.data);
    } catch (error) {
      console.error("Error fetching bank accounts:", error);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API_URL}/transactions`);
      setTransactions(response.data);
    } catch (error) {
      console.error("Error fetching transactions:", error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API_URL}/vendors`);
      setVendors(response.data);
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  };

  const handleBankSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/bank-accounts`, {
        ...bankForm,
        opening_balance: parseFloat(bankForm.opening_balance) || 0
      });
      toast.success("Bank account added successfully");
      setBankDialogOpen(false);
      resetBankForm();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add bank account");
    }
  };

  const handleTransactionSubmit = async (e) => {
    e.preventDefault();
    try {
      // Expenses are treated as payments (money out) in the backend
      const submitData = {
        ...transactionForm,
        amount: parseFloat(transactionForm.amount),
        // Convert expense type to payment for backend processing
        transaction_type: transactionForm.transaction_type === 'expense' ? 'payment' : transactionForm.transaction_type
      };
      
      await axios.post(`${API_URL}/transactions`, submitData);
      toast.success(transactionForm.transaction_type === 'expense' ? "Expense recorded successfully" : "Transaction recorded successfully");
      setTransactionDialogOpen(false);
      resetTransactionForm();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record transaction");
    }
  };

  const handleDelete = async () => {
    try {
      if (deleteType === "bank") {
        await axios.delete(`${API_URL}/bank-accounts/${selectedItem.id}`);
        toast.success("Bank account deleted");
      } else if (deleteType === "transaction") {
        await axios.delete(`${API_URL}/transactions/${selectedItem.id}`);
        toast.success("Transaction deleted");
      }
      setDeleteDialogOpen(false);
      setSelectedItem(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  const resetBankForm = () => {
    setBankForm({
      account_name: "",
      bank_name: "",
      account_number: "",
      ifsc_code: "",
      branch: "",
      account_type: "current",
      opening_balance: 0
    });
  };

  const resetTransactionForm = () => {
    setTransactionForm({
      transaction_type: "receipt",
      category: "client_advance",
      amount: "",
      bank_account_id: "",
      reference_type: "client",
      reference_id: "",
      vehicle_number: "",
      description: "",
      transaction_date: new Date().toISOString().split('T')[0],
      payment_mode: "bank_transfer"
    });
  };

  // Open edit transaction dialog
  const openEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setEditTransactionForm({
      transaction_type: transaction.transaction_type || "",
      category: transaction.category || "",
      amount: transaction.amount?.toString() || "",
      bank_account_id: transaction.bank_account_id || "",
      reference_type: transaction.reference_type || "",
      reference_id: transaction.reference_id || "",
      vehicle_number: transaction.vehicle_number || "",
      description: transaction.description || "",
      transaction_date: transaction.transaction_date || "",
      payment_mode: transaction.payment_mode || ""
    });
    setEditTransactionDialogOpen(true);
  };

  // Handle edit transaction submit
  const handleEditTransactionSubmit = async (e) => {
    e.preventDefault();
    if (!editingTransaction) return;
    
    try {
      const updateData = {
        transaction_type: editTransactionForm.transaction_type,
        category: editTransactionForm.category,
        amount: parseFloat(editTransactionForm.amount),
        bank_account_id: editTransactionForm.bank_account_id || null,
        reference_type: editTransactionForm.reference_type || null,
        reference_id: editTransactionForm.reference_id || null,
        vehicle_number: editTransactionForm.vehicle_number || null,
        description: editTransactionForm.description || null,
        transaction_date: editTransactionForm.transaction_date,
        payment_mode: editTransactionForm.payment_mode || null
      };
      
      await axios.put(`${API_URL}/transactions/${editingTransaction.id}`, updateData);
      toast.success("Transaction updated successfully");
      setEditTransactionDialogOpen(false);
      setEditingTransaction(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update transaction");
    }
  };

  // Client Ledger handlers
  const handleReceivePayment = async (e) => {
    e.preventDefault();
    try {
      const params = new URLSearchParams({
        client_id: paymentForm.client_id,
        amount: paymentForm.amount,
        payment_type: paymentForm.payment_type,
        payment_mode: paymentForm.payment_mode
      });
      
      if (paymentForm.bank_account_id) params.append("bank_account_id", paymentForm.bank_account_id);
      if (paymentForm.utr_number) params.append("utr_number", paymentForm.utr_number);
      if (paymentForm.bank_name) params.append("bank_name", paymentForm.bank_name);
      if (paymentForm.description) params.append("description", paymentForm.description);
      
      // TDS parameters
      if (paymentForm.tds_applicable) {
        params.append("tds_applicable", "true");
        if (paymentForm.tds_rate) params.append("tds_rate", paymentForm.tds_rate);
      }

      const response = await axios.post(`${API_URL}/client-ledger/receive-payment?${params.toString()}`);
      const tdsMsg = response.data.tds_applicable ? ` (TDS ${response.data.tds_rate}%: ₹${response.data.tds_amount} deducted)` : "";
      toast.success(paymentForm.utr_number ? `Payment confirmed with UTR${tdsMsg}` : `Payment recorded - pending UTR confirmation${tdsMsg}`);
      setPaymentDialogOpen(false);
      resetPaymentForm();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record payment");
    }
  };

  const handleConfirmUtr = async (e) => {
    e.preventDefault();
    if (!selectedLedgerEntry) return;
    
    try {
      const params = new URLSearchParams({
        utr_number: utrForm.utr_number,
        payment_mode: utrForm.payment_mode
      });
      if (utrForm.bank_name) params.append("bank_name", utrForm.bank_name);
      
      await axios.put(`${API_URL}/client-ledger/${selectedLedgerEntry.id}/confirm-utr?${params.toString()}`);
      toast.success("UTR confirmed successfully!");
      setUtrDialogOpen(false);
      setSelectedLedgerEntry(null);
      setUtrForm({ utr_number: "", payment_mode: "bank_transfer", bank_name: "" });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to confirm UTR");
    }
  };

  const resetPaymentForm = () => {
    setPaymentForm({
      client_id: "",
      amount: "",
      payment_type: "balance",
      payment_mode: "bank_transfer",
      bank_account_id: "",
      utr_number: "",
      bank_name: "",
      description: "",
      tds_applicable: false,
      tds_rate: ""
    });
  };

  const openUtrDialog = (entry) => {
    setSelectedLedgerEntry(entry);
    setUtrForm({ 
      utr_number: entry.utr_number || "", 
      payment_mode: entry.payment_mode || "bank_transfer", 
      bank_name: entry.bank_name || "" 
    });
    setUtrDialogOpen(true);
  };

  // Vendor Ledger handlers
  const handleVendorPayment = async (e) => {
    e.preventDefault();
    try {
      const params = new URLSearchParams({
        vendor_id: vendorPaymentForm.vendor_id,
        amount: vendorPaymentForm.amount,
        payment_mode: vendorPaymentForm.payment_mode,
        bank_account_id: vendorPaymentForm.bank_account_id
      });
      if (vendorPaymentForm.description) params.append("description", vendorPaymentForm.description);
      if (vendorPaymentForm.vehicle_number) params.append("vehicle_number", vendorPaymentForm.vehicle_number);
      if (vendorPaymentForm.utr_number) params.append("utr_number", vendorPaymentForm.utr_number);
      
      // TDS parameters
      if (vendorPaymentForm.tds_applicable) {
        params.append("tds_applicable", "true");
        if (vendorPaymentForm.tds_rate) params.append("tds_rate", vendorPaymentForm.tds_rate);
      }
      
      const response = await axios.post(`${API_URL}/vendor-ledger/make-payment?${params.toString()}`);
      const tdsMsg = response.data.tds_applicable ? ` (TDS ${response.data.tds_rate}%: ₹${response.data.tds_amount} deducted)` : "";
      toast.success(`Payment to vendor recorded successfully${tdsMsg}`);
      setVendorPaymentDialogOpen(false);
      setVendorPaymentForm({
        vendor_id: "",
        amount: "",
        payment_mode: "bank_transfer",
        bank_account_id: "",
        utr_number: "",
        description: "",
        vehicle_number: "",
        tds_applicable: false,
        tds_rate: "",
        total_freight: 0,
        total_advance: 0,
        outstanding: 0
      });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record payment");
    }
  };

  const handleVendorUtrConfirm = async (e) => {
    e.preventDefault();
    if (!selectedVendorEntry) return;
    
    try {
      await axios.put(`${API_URL}/vendor-ledger/${selectedVendorEntry.id}/confirm-utr?utr_number=${vendorPaymentForm.utr_number}`);
      toast.success("Payment confirmed with UTR");
      setVendorUtrDialogOpen(false);
      setSelectedVendorEntry(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to confirm UTR");
    }
  };

  const openVendorPaymentDialog = (vendor = null, autoFillAmount = false) => {
    if (vendor) {
      // Find vendor in outstanding data to get financial summary
      const vendorData = vendorOutstandingData.vendors?.find(
        v => v.vendor_id === (vendor.id || vendor.vendor_id)
      );
      const outstandingAmount = vendorData?.outstanding || 0;
      setVendorPaymentForm({
        ...vendorPaymentForm,
        vendor_id: vendor.id || vendor.vendor_id,
        description: `Payment to ${vendor.name || vendor.vendor_name}`,
        // Auto-fill amount if requested (Quick Pay feature)
        amount: autoFillAmount && outstandingAmount > 0 ? outstandingAmount.toString() : "",
        // Set financial summary from outstanding data
        total_freight: vendorData?.total_payable || 0,
        total_advance: vendorData?.total_advance_paid || 0,
        outstanding: outstandingAmount
      });
    } else {
      // Reset financial summary when no vendor selected
      setVendorPaymentForm({
        ...vendorPaymentForm,
        vendor_id: "",
        amount: "",
        description: "",
        total_freight: 0,
        total_advance: 0,
        outstanding: 0
      });
    }
    setVendorPaymentDialogOpen(true);
  };

  const openVendorUtrDialog = (entry) => {
    setSelectedVendorEntry(entry);
    setVendorPaymentForm({ ...vendorPaymentForm, utr_number: entry.utr_number || "" });
    setVendorUtrDialogOpen(true);
  };

  const getVendorLedgerEntryTypeBadge = (type) => {
    switch (type) {
      case "payable": return "bg-orange-500/20 text-orange-400 border-orange-500/50";
      case "advance_paid": return "bg-blue-500/20 text-blue-400 border-blue-500/50";
      case "balance_paid": return "bg-green-500/20 text-green-400 border-green-500/50";
      default: return "bg-gray-500/20 text-gray-400 border-gray-500/50";
    }
  };

  const getVendorLedgerEntryTypeLabel = (type) => {
    switch (type) {
      case "payable": return "Amount Due";
      case "advance_paid": return "Advance Paid";
      case "balance_paid": return "Balance Paid";
      default: return type;
    }
  };

  const getLedgerEntryTypeBadge = (type) => {
    switch (type) {
      case "receivable": return "bg-orange-500/20 text-orange-400 border-orange-500/50";
      case "advance_received": return "bg-blue-500/20 text-blue-400 border-blue-500/50";
      case "balance_received": return "bg-green-500/20 text-green-400 border-green-500/50";
      default: return "bg-gray-500/20 text-gray-400 border-gray-500/50";
    }
  };

  const getLedgerEntryTypeLabel = (type) => {
    switch (type) {
      case "receivable": return "Freight Due";
      case "advance_received": return "Advance";
      case "balance_received": return "Balance";
      default: return type;
    }
  };

  const filteredTransactions = transactions.filter(t => {
    if (transactionFilter.type !== "all" && t.transaction_type !== transactionFilter.type) return false;
    if (transactionFilter.category !== "all" && t.category !== transactionFilter.category) return false;
    if (transactionFilter.bankAccountId !== "all" && t.bank_account_id !== transactionFilter.bankAccountId) return false;
    return true;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getCategoryLabel = (category) => {
    const labels = {
      client_advance: "Client Advance",
      client_balance: "Client Balance",
      vendor_advance: "Vendor Advance",
      vendor_balance: "Vendor Balance",
      expense_salary: "Employee Salary",
      expense_stationery: "Stationery",
      expense_office: "Office Expense",
      expense_fuel: "Fuel Expense",
      expense_maintenance: "Maintenance",
      expense_other: "Other Expense",
      other: "Other"
    };
    return labels[category] || category;
  };

  const getCategoryBadgeStyle = (category) => {
    if (category.startsWith("client")) {
      return "bg-green-500/20 text-green-400 border-green-500/50";
    } else if (category.startsWith("vendor")) {
      return "bg-orange-500/20 text-orange-400 border-orange-500/50";
    } else if (category.startsWith("expense")) {
      return "bg-red-500/20 text-red-400 border-red-500/50";
    }
    return "bg-gray-500/20 text-gray-400 border-gray-500/50";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="accounting-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Wallet className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-heading font-bold">Accounting</h1>
            <p className="text-muted-foreground text-sm">Manage payments, receipts, and bank accounts</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setBankDialogOpen(true)} variant="outline" data-testid="add-bank-btn">
            <Building className="w-4 h-4 mr-2" />
            Add Bank
          </Button>
          <Button onClick={() => setTransactionDialogOpen(true)} className="bg-primary" data-testid="add-transaction-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Transaction
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <ArrowDownCircle className="w-4 h-4 text-green-400" />
                Total Receipts
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-400">{formatCurrency(summary.total_receipts)}</div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <ArrowUpCircle className="w-4 h-4 text-orange-400" />
                Total Payments
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-400">{formatCurrency(summary.total_payments)}</div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-primary" />
                Net Balance
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${summary.net_balance >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {formatCurrency(summary.net_balance)}
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-blue-400" />
                Bank Accounts
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-400">{bankAccounts.length}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-secondary/50 flex-wrap">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="profit-loss" data-testid="tab-profit-loss" className="flex items-center gap-1">
            <TrendingUp className="w-3 h-3" />
            Profit & Loss
          </TabsTrigger>
          <TabsTrigger value="receipts" data-testid="tab-receipts">Receipts (In)</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Payments (Out)</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses">Expenses</TabsTrigger>
          <TabsTrigger value="banks" data-testid="tab-banks">Bank Accounts</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">All Transactions</TabsTrigger>
          <TabsTrigger value="client-ledger" data-testid="tab-client-ledger" className="flex items-center gap-1">
            <BookOpen className="w-4 h-4" />
            Client Ledger
            {outstandingData.total_pending_utr > 0 && (
              <Badge className="ml-1 bg-yellow-500/20 text-yellow-400 text-xs">{outstandingData.total_pending_utr}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="vendor-ledger" data-testid="tab-vendor-ledger" className="flex items-center gap-1">
            <Truck className="w-4 h-4" />
            Vendor Ledger
            {vendorOutstandingData.total_pending_payments > 0 && (
              <Badge className="ml-1 bg-yellow-500/20 text-yellow-400 text-xs">{vendorOutstandingData.total_pending_payments}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="vendor-accounts" data-testid="tab-vendor-accounts" className="flex items-center gap-1">
            <Truck className="w-4 h-4" />
            Vendor Accounts
          </TabsTrigger>
          <TabsTrigger value="tds-report" data-testid="tab-tds-report" className="flex items-center gap-1">
            <Percent className="w-4 h-4" />
            TDS Report
          </TabsTrigger>
          <TabsTrigger value="bank-transfers" data-testid="tab-bank-transfers" className="flex items-center gap-1 bg-blue-600/20 text-blue-400">
            <Send className="w-4 h-4" />
            Bank Transfers
            {pendingTransfers.length > 0 && (
              <Badge className="ml-1 bg-orange-500/20 text-orange-400 text-xs">{pendingTransfers.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          {/* Bank Balances */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg">Bank Account Balances</CardTitle>
            </CardHeader>
            <CardContent>
              {summary?.bank_balances?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {summary.bank_balances.map((bank) => (
                    <div key={bank.id} className="p-4 bg-secondary/30 rounded-lg border border-border">
                      <div className="flex items-center gap-2 mb-2">
                        <Building className="w-4 h-4 text-primary" />
                        <span className="font-medium">{bank.account_name}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">{bank.bank_name} - {bank.account_number}</p>
                      <div className={`text-xl font-bold ${bank.current_balance >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatCurrency(bank.current_balance)}
                      </div>
                      <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="text-green-400">In: {formatCurrency(bank.total_receipts)}</span>
                        <span className="text-orange-400">Out: {formatCurrency(bank.total_payments)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">No bank accounts added yet</p>
              )}
            </CardContent>
          </Card>

          {/* Recent Transactions */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg">Recent Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              {summary?.recent_transactions?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="border-border">
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead>Vehicle</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {summary.recent_transactions.map((trans) => (
                      <TableRow key={trans.id} className="border-border">
                        <TableCell>{new Date(trans.transaction_date).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={trans.transaction_type === 'receipt' ? 'text-green-400 border-green-500/50' : 'text-orange-400 border-orange-500/50'}>
                            {trans.transaction_type === 'receipt' ? 'Receipt' : 'Payment'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={getCategoryBadgeStyle(trans.category)}>
                            {getCategoryLabel(trans.category)}
                          </Badge>
                        </TableCell>
                        <TableCell>{trans.reference_name || '-'}</TableCell>
                        <TableCell>{trans.vehicle_number || '-'}</TableCell>
                        <TableCell className={`text-right font-medium ${trans.transaction_type === 'receipt' ? 'text-green-400' : 'text-orange-400'}`}>
                          {trans.transaction_type === 'receipt' ? '+' : '-'}{formatCurrency(trans.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-muted-foreground text-center py-8">No transactions yet</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Profit & Loss Tab */}
        <TabsContent value="profit-loss" className="space-y-4">
          {/* Date Filter & Actions */}
          <Card className="bg-card border-border">
            <CardContent className="pt-4">
              <div className="flex flex-wrap items-center gap-4">
                {/* Quick Period Buttons */}
                <div className="flex flex-wrap gap-2">
                  <Button 
                    variant={plDateFilter.period === 'today' ? 'default' : 'outline'} 
                    size="sm"
                    onClick={() => handlePLPeriodChange('today')}
                  >
                    Today
                  </Button>
                  <Button 
                    variant={plDateFilter.period === 'week' ? 'default' : 'outline'} 
                    size="sm"
                    onClick={() => handlePLPeriodChange('week')}
                  >
                    This Week
                  </Button>
                  <Button 
                    variant={plDateFilter.period === 'month' ? 'default' : 'outline'} 
                    size="sm"
                    onClick={() => handlePLPeriodChange('month')}
                  >
                    This Month
                  </Button>
                  <Button 
                    variant={plDateFilter.period === 'quarter' ? 'default' : 'outline'} 
                    size="sm"
                    onClick={() => handlePLPeriodChange('quarter')}
                  >
                    This Quarter
                  </Button>
                  <Button 
                    variant={plDateFilter.period === 'year' ? 'default' : 'outline'} 
                    size="sm"
                    onClick={() => handlePLPeriodChange('year')}
                  >
                    This Year
                  </Button>
                </div>
                
                {/* Custom Date Range */}
                <div className="flex items-center gap-2 ml-auto">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <Input
                    type="date"
                    value={plDateFilter.startDate}
                    onChange={(e) => setPlDateFilter({ ...plDateFilter, startDate: e.target.value, period: 'custom' })}
                    className="w-36 h-8"
                  />
                  <span className="text-muted-foreground">to</span>
                  <Input
                    type="date"
                    value={plDateFilter.endDate}
                    onChange={(e) => setPlDateFilter({ ...plDateFilter, endDate: e.target.value, period: 'custom' })}
                    className="w-36 h-8"
                  />
                  <Button size="sm" onClick={applyPLDateFilter}>
                    <Filter className="w-4 h-4 mr-1" />
                    Apply
                  </Button>
                </div>
                
                {/* Actions */}
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={exportPLToCSV}>
                    <Download className="w-4 h-4 mr-1" />
                    Export CSV
                  </Button>
                  <Button 
                    variant="outline"
                    size="sm" 
                    onClick={exportPLToPDF}
                  >
                    <FileText className="w-4 h-4 mr-1" />
                    Export PDF
                  </Button>
                  <Button 
                    variant={plComparison.enabled ? 'default' : 'outline'} 
                    size="sm" 
                    onClick={getMonthlyComparison}
                  >
                    <BarChart3 className="w-4 h-4 mr-1" />
                    Monthly Comparison
                  </Button>
                  <Button 
                    variant={quarterlyData.length > 0 ? 'default' : 'outline'} 
                    size="sm" 
                    onClick={getQuarterlyReport}
                  >
                    <PieChart className="w-4 h-4 mr-1" />
                    Quarterly Report
                  </Button>
                  <Button 
                    variant={yoyData.length > 0 ? 'default' : 'outline'} 
                    size="sm" 
                    onClick={getYearOverYearComparison}
                  >
                    <TrendingUpIcon className="w-4 h-4 mr-1" />
                    Year-over-Year
                  </Button>
                  <Button 
                    variant={clientProfitability.length > 0 ? 'default' : 'outline'} 
                    size="sm" 
                    onClick={getClientProfitability}
                  >
                    <Users2 className="w-4 h-4 mr-1" />
                    Client Analysis
                  </Button>
                  <Button 
                    variant="outline"
                    size="sm" 
                    onClick={() => setEmailDialogOpen(true)}
                  >
                    <Mail className="w-4 h-4 mr-1" />
                    Email Report
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Visual Chart */}
          {showChart && chartData.length > 0 && (
            <Card className="bg-card border-border">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-primary" />
                    P&L Trend Chart
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button 
                      variant={chartType === 'area' ? 'default' : 'outline'} 
                      size="sm"
                      onClick={() => setChartType('area')}
                    >
                      Area
                    </Button>
                    <Button 
                      variant={chartType === 'bar' ? 'default' : 'outline'} 
                      size="sm"
                      onClick={() => setChartType('bar')}
                    >
                      Bar
                    </Button>
                    <Button 
                      variant={chartType === 'line' ? 'default' : 'outline'} 
                      size="sm"
                      onClick={() => setChartType('line')}
                    >
                      Line
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    {chartType === 'area' ? (
                      <AreaChart data={chartData}>
                        <defs>
                          <linearGradient id="colorFreight" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#22c55e" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#22c55e" stopOpacity={0.1}/>
                          </linearGradient>
                          <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="month" stroke="#9ca3af" />
                        <YAxis stroke="#9ca3af" />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                          labelStyle={{ color: '#f3f4f6' }}
                        />
                        <Legend />
                        <Area type="monotone" dataKey="freight" stroke="#22c55e" fillOpacity={1} fill="url(#colorFreight)" name="Freight (₹)" />
                        <Area type="monotone" dataKey="profit" stroke="#3b82f6" fillOpacity={1} fill="url(#colorProfit)" name="Profit (₹)" />
                      </AreaChart>
                    ) : chartType === 'bar' ? (
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="month" stroke="#9ca3af" />
                        <YAxis stroke="#9ca3af" />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                          labelStyle={{ color: '#f3f4f6' }}
                        />
                        <Legend />
                        <Bar dataKey="freight" fill="#22c55e" name="Freight (₹)" />
                        <Bar dataKey="vendorCost" fill="#ef4444" name="Vendor Cost (₹)" />
                        <Bar dataKey="profit" fill="#3b82f6" name="Profit (₹)" />
                      </BarChart>
                    ) : (
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="month" stroke="#9ca3af" />
                        <YAxis stroke="#9ca3af" />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                          labelStyle={{ color: '#f3f4f6' }}
                        />
                        <Legend />
                        <Line type="monotone" dataKey="freight" stroke="#22c55e" strokeWidth={2} dot={{ fill: '#22c55e' }} name="Freight (₹)" />
                        <Line type="monotone" dataKey="vendorCost" stroke="#ef4444" strokeWidth={2} dot={{ fill: '#ef4444' }} name="Vendor Cost (₹)" />
                        <Line type="monotone" dataKey="profit" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6' }} name="Profit (₹)" />
                      </LineChart>
                    )}
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quarterly Report */}
          {quarterlyData.length > 0 && (
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-purple-400" />
                  Quarterly P&L Report (Last 4 Quarters)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Quarter</TableHead>
                        <TableHead>Period</TableHead>
                        <TableHead className="text-right">Orders</TableHead>
                        <TableHead className="text-right text-green-400">Freight (₹)</TableHead>
                        <TableHead className="text-right text-red-400">Vendor Cost (₹)</TableHead>
                        <TableHead className="text-right text-blue-400">Profit (₹)</TableHead>
                        <TableHead className="text-right">Margin</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {quarterlyData.map((q, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-semibold text-purple-400">{q.quarter}</TableCell>
                          <TableCell className="text-muted-foreground">{q.period}</TableCell>
                          <TableCell className="text-right">{q.orders}</TableCell>
                          <TableCell className="text-right text-green-400">{q.freight.toLocaleString()}</TableCell>
                          <TableCell className="text-right text-red-400">{q.vendorCost.toLocaleString()}</TableCell>
                          <TableCell className={`text-right font-semibold ${q.profit >= 0 ? 'text-blue-400' : 'text-red-400'}`}>
                            {q.profit.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right">{q.margin}%</TableCell>
                        </TableRow>
                      ))}
                      {/* Total Row */}
                      <TableRow className="bg-purple-500/10 font-bold">
                        <TableCell colSpan={2}>TOTAL (4 Quarters)</TableCell>
                        <TableCell className="text-right">{quarterlyData.reduce((s, r) => s + r.orders, 0)}</TableCell>
                        <TableCell className="text-right text-green-400">
                          {quarterlyData.reduce((s, r) => s + r.freight, 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right text-red-400">
                          {quarterlyData.reduce((s, r) => s + r.vendorCost, 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right text-blue-400">
                          {quarterlyData.reduce((s, r) => s + r.profit, 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {(() => {
                            const totalFreight = quarterlyData.reduce((s, r) => s + r.freight, 0);
                            const totalProfit = quarterlyData.reduce((s, r) => s + r.profit, 0);
                            return totalFreight > 0 ? ((totalProfit / totalFreight) * 100).toFixed(1) : 0;
                          })()}%
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Year-over-Year Comparison */}
          {yoyData.length > 0 && (
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUpIcon className="w-5 h-5 text-orange-400" />
                  Year-over-Year Comparison
                </CardTitle>
                <CardDescription>Compare performance across years</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Year</TableHead>
                        <TableHead className="text-right">Orders</TableHead>
                        <TableHead className="text-right text-green-400">Freight (₹)</TableHead>
                        <TableHead className="text-right text-red-400">Vendor Cost (₹)</TableHead>
                        <TableHead className="text-right text-blue-400">Profit (₹)</TableHead>
                        <TableHead className="text-right">Margin</TableHead>
                        <TableHead className="text-right">Freight Growth</TableHead>
                        <TableHead className="text-right">Profit Growth</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {yoyData.map((y, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-semibold text-orange-400">{y.year}</TableCell>
                          <TableCell className="text-right">{y.orders}</TableCell>
                          <TableCell className="text-right text-green-400">{y.freight.toLocaleString()}</TableCell>
                          <TableCell className="text-right text-red-400">{y.vendorCost.toLocaleString()}</TableCell>
                          <TableCell className={`text-right font-semibold ${y.profit >= 0 ? 'text-blue-400' : 'text-red-400'}`}>
                            {y.profit.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right">{y.margin}%</TableCell>
                          <TableCell className="text-right">
                            {y.freightGrowth !== null ? (
                              <span className={parseFloat(y.freightGrowth) >= 0 ? 'text-green-400' : 'text-red-400'}>
                                {parseFloat(y.freightGrowth) >= 0 ? '↑' : '↓'} {Math.abs(y.freightGrowth)}%
                              </span>
                            ) : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            {y.profitGrowth !== null ? (
                              <span className={parseFloat(y.profitGrowth) >= 0 ? 'text-green-400' : 'text-red-400'}>
                                {parseFloat(y.profitGrowth) >= 0 ? '↑' : '↓'} {Math.abs(y.profitGrowth)}%
                              </span>
                            ) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Client-wise Profitability Analysis */}
          {clientProfitability.length > 0 && (
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users2 className="w-5 h-5 text-cyan-400" />
                  Client-wise Profitability Analysis
                </CardTitle>
                <CardDescription>Ranked by total profit contribution</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Rank</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead className="text-right">Orders</TableHead>
                        <TableHead className="text-right text-green-400">Total Freight (₹)</TableHead>
                        <TableHead className="text-right text-red-400">Total Vendor Cost (₹)</TableHead>
                        <TableHead className="text-right text-blue-400">Total Profit (₹)</TableHead>
                        <TableHead className="text-right">Avg Freight/Order</TableHead>
                        <TableHead className="text-right">Avg Profit/Order</TableHead>
                        <TableHead className="text-right">Margin</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {clientProfitability.map((c, idx) => (
                        <TableRow key={idx} className={idx === 0 ? 'bg-yellow-500/10' : idx === 1 ? 'bg-gray-500/10' : idx === 2 ? 'bg-orange-500/10' : ''}>
                          <TableCell>
                            <span className={`font-bold ${idx === 0 ? 'text-yellow-400' : idx === 1 ? 'text-gray-400' : idx === 2 ? 'text-orange-400' : ''}`}>
                              #{idx + 1}
                            </span>
                          </TableCell>
                          <TableCell className="font-semibold">{c.client_name}</TableCell>
                          <TableCell className="text-right">{c.orderCount}</TableCell>
                          <TableCell className="text-right text-green-400">{c.totalFreight.toLocaleString()}</TableCell>
                          <TableCell className="text-right text-red-400">{c.totalVendorCost.toLocaleString()}</TableCell>
                          <TableCell className={`text-right font-semibold ${c.totalProfit >= 0 ? 'text-blue-400' : 'text-red-400'}`}>
                            {c.totalProfit.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right">₹{parseInt(c.avgFreightPerOrder).toLocaleString()}</TableCell>
                          <TableCell className="text-right">₹{parseInt(c.avgProfitPerOrder).toLocaleString()}</TableCell>
                          <TableCell className="text-right">{c.margin}%</TableCell>
                        </TableRow>
                      ))}
                      {/* Total Row */}
                      <TableRow className="bg-cyan-500/10 font-bold">
                        <TableCell></TableCell>
                        <TableCell>TOTAL ({clientProfitability.length} Clients)</TableCell>
                        <TableCell className="text-right">{clientProfitability.reduce((s, c) => s + c.orderCount, 0)}</TableCell>
                        <TableCell className="text-right text-green-400">
                          {clientProfitability.reduce((s, c) => s + c.totalFreight, 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right text-red-400">
                          {clientProfitability.reduce((s, c) => s + c.totalVendorCost, 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right text-blue-400">
                          {clientProfitability.reduce((s, c) => s + c.totalProfit, 0).toLocaleString()}
                        </TableCell>
                        <TableCell colSpan={3}></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Monthly Comparison Table */}
          {plComparison.enabled && plComparison.data.length > 0 && (
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  Monthly P&L Comparison (Last 6 Months)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Month</TableHead>
                        <TableHead className="text-right">Orders</TableHead>
                        <TableHead className="text-right text-green-400">Freight (₹)</TableHead>
                        <TableHead className="text-right text-red-400">Vendor Cost (₹)</TableHead>
                        <TableHead className="text-right text-blue-400">Profit (₹)</TableHead>
                        <TableHead className="text-right">Margin</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {plComparison.data.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{row.month}</TableCell>
                          <TableCell className="text-right">{row.orders}</TableCell>
                          <TableCell className="text-right text-green-400">{row.freight.toLocaleString()}</TableCell>
                          <TableCell className="text-right text-red-400">{row.vendorCost.toLocaleString()}</TableCell>
                          <TableCell className={`text-right font-semibold ${row.profit >= 0 ? 'text-blue-400' : 'text-red-400'}`}>
                            {row.profit.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right">
                            {row.freight > 0 ? ((row.profit / row.freight) * 100).toFixed(1) : 0}%
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Total Row */}
                      <TableRow className="bg-secondary/50 font-bold">
                        <TableCell>TOTAL</TableCell>
                        <TableCell className="text-right">{plComparison.data.reduce((s, r) => s + r.orders, 0)}</TableCell>
                        <TableCell className="text-right text-green-400">
                          {plComparison.data.reduce((s, r) => s + r.freight, 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right text-red-400">
                          {plComparison.data.reduce((s, r) => s + r.vendorCost, 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right text-blue-400">
                          {plComparison.data.reduce((s, r) => s + r.profit, 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {(() => {
                            const totalFreight = plComparison.data.reduce((s, r) => s + r.freight, 0);
                            const totalProfit = plComparison.data.reduce((s, r) => s + r.profit, 0);
                            return totalFreight > 0 ? ((totalProfit / totalFreight) * 100).toFixed(1) : 0;
                          })()}%
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* P&L Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Card className="bg-gradient-to-br from-green-500/20 to-green-600/10 border-green-500/30">
              <CardHeader className="pb-2">
                <CardDescription className="text-green-300">Total Freight (Income)</CardDescription>
                <CardTitle className="text-2xl text-green-400">
                  ₹{profitLossData.totalFreight.toLocaleString()}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">From {profitLossData.orderWisePL.length} orders</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-red-500/20 to-red-600/10 border-red-500/30">
              <CardHeader className="pb-2">
                <CardDescription className="text-red-300">Vendor Cost</CardDescription>
                <CardTitle className="text-2xl text-red-400">
                  ₹{profitLossData.totalVendorCost.toLocaleString()}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">Paid to vendors</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border-blue-500/30">
              <CardHeader className="pb-2">
                <CardDescription className="text-blue-300">Gross Profit</CardDescription>
                <CardTitle className={`text-2xl ${profitLossData.grossProfit >= 0 ? 'text-blue-400' : 'text-red-400'}`}>
                  ₹{profitLossData.grossProfit.toLocaleString()}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">Margin: {profitLossData.profitMargin}%</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-orange-500/20 to-orange-600/10 border-orange-500/30">
              <CardHeader className="pb-2">
                <CardDescription className="text-orange-300">Operating Expenses</CardDescription>
                <CardTitle className="text-2xl text-orange-400">
                  ₹{profitLossData.expenses.toLocaleString()}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">
                  {profitLossData.staffSalary > 0 ? `Salary: ₹${profitLossData.staffSalary.toLocaleString()}` : 'No expenses'}
                </p>
              </CardContent>
            </Card>
            
            <Card className={`bg-gradient-to-br ${profitLossData.netProfit >= 0 ? 'from-emerald-500/20 to-emerald-600/10 border-emerald-500/30' : 'from-red-500/20 to-red-600/10 border-red-500/30'}`}>
              <CardHeader className="pb-2">
                <CardDescription className={profitLossData.netProfit >= 0 ? 'text-emerald-300' : 'text-red-300'}>
                  Net Profit/Loss
                </CardDescription>
                <CardTitle className={`text-2xl ${profitLossData.netProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  ₹{profitLossData.netProfit.toLocaleString()}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">Final Profit</p>
              </CardContent>
            </Card>
          </div>
          
          {/* P&L Statement */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Profit & Loss Statement
              </CardTitle>
              <CardDescription>Freight Income vs Vendor Cost breakdown</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Income Section */}
                <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                  <h4 className="font-semibold text-green-400 mb-2">INCOME</h4>
                  <div className="flex justify-between items-center">
                    <span>Freight Charges (Client Rate)</span>
                    <span className="font-bold text-green-400">₹{profitLossData.totalFreight.toLocaleString()}</span>
                  </div>
                </div>
                
                {/* Expense Section */}
                <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                  <h4 className="font-semibold text-red-400 mb-2">DIRECT COSTS</h4>
                  <div className="flex justify-between items-center mb-2">
                    <span>Vendor Rate (Transport Cost)</span>
                    <span className="font-bold text-red-400">₹{profitLossData.totalVendorCost.toLocaleString()}</span>
                  </div>
                </div>
                
                {/* Gross Profit */}
                <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-blue-400">GROSS PROFIT</span>
                    <span className={`font-bold text-xl ${profitLossData.grossProfit >= 0 ? 'text-blue-400' : 'text-red-400'}`}>
                      ₹{profitLossData.grossProfit.toLocaleString()}
                    </span>
                  </div>
                </div>
                
                {/* Operating Expenses - Detailed Breakdown */}
                <div className="p-4 bg-orange-500/10 rounded-lg border border-orange-500/30">
                  <h4 className="font-semibold text-orange-400 mb-3">OPERATING EXPENSES</h4>
                  <div className="space-y-2">
                    {profitLossData.staffSalary > 0 && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-muted-foreground">Staff Salary</span>
                        <span className="text-orange-300">₹{profitLossData.staffSalary.toLocaleString()}</span>
                      </div>
                    )}
                    {profitLossData.officeExpense > 0 && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-muted-foreground">Office & Stationery</span>
                        <span className="text-orange-300">₹{profitLossData.officeExpense.toLocaleString()}</span>
                      </div>
                    )}
                    {profitLossData.fuelExpense > 0 && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-muted-foreground">Fuel Expense</span>
                        <span className="text-orange-300">₹{profitLossData.fuelExpense.toLocaleString()}</span>
                      </div>
                    )}
                    {profitLossData.maintenanceExpense > 0 && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-muted-foreground">Maintenance</span>
                        <span className="text-orange-300">₹{profitLossData.maintenanceExpense.toLocaleString()}</span>
                      </div>
                    )}
                    {profitLossData.otherExpense > 0 && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-muted-foreground">Other Expenses</span>
                        <span className="text-orange-300">₹{profitLossData.otherExpense.toLocaleString()}</span>
                      </div>
                    )}
                    {profitLossData.expenses === 0 && (
                      <div className="text-sm text-muted-foreground italic">No expenses recorded</div>
                    )}
                    <div className="flex justify-between items-center pt-2 border-t border-orange-500/30">
                      <span className="font-semibold">Total Operating Expenses</span>
                      <span className="font-bold text-orange-400">₹{profitLossData.expenses.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
                
                {/* Net Profit */}
                <div className={`p-4 rounded-lg border ${profitLossData.netProfit >= 0 ? 'bg-emerald-500/20 border-emerald-500/50' : 'bg-red-500/20 border-red-500/50'}`}>
                  <div className="flex justify-between items-center">
                    <span className={`font-bold text-lg ${profitLossData.netProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      NET {profitLossData.netProfit >= 0 ? 'PROFIT' : 'LOSS'}
                    </span>
                    <span className={`font-bold text-2xl ${profitLossData.netProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      ₹{Math.abs(profitLossData.netProfit).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Order-wise P&L Table */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Truck className="w-5 h-5 text-primary" />
                Order-wise Profit & Loss
              </CardTitle>
              <CardDescription>Detailed breakdown by each order</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/50">
                      <TableHead className="text-muted-foreground">Order No.</TableHead>
                      <TableHead className="text-muted-foreground">Client</TableHead>
                      <TableHead className="text-muted-foreground">Vendor</TableHead>
                      <TableHead className="text-muted-foreground text-right">Freight (₹)</TableHead>
                      <TableHead className="text-muted-foreground text-right">Vendor Cost (₹)</TableHead>
                      <TableHead className="text-muted-foreground text-right">Profit (₹)</TableHead>
                      <TableHead className="text-muted-foreground text-right">Margin %</TableHead>
                      <TableHead className="text-muted-foreground">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {profitLossData.orderWisePL.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          No orders with freight data found
                        </TableCell>
                      </TableRow>
                    ) : (
                      profitLossData.orderWisePL.map((order) => (
                        <TableRow key={order.id} className="border-border/50">
                          <TableCell className="font-mono text-primary">{order.order_number}</TableCell>
                          <TableCell>{order.client_name}</TableCell>
                          <TableCell>{order.vendor_name}</TableCell>
                          <TableCell className="text-right text-green-400">
                            {order.freight_amount.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right text-red-400">
                            {order.vendor_cost.toLocaleString()}
                          </TableCell>
                          <TableCell className={`text-right font-semibold ${order.profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {order.profit.toLocaleString()}
                          </TableCell>
                          <TableCell className={`text-right ${order.profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {order.profit_margin}%
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={
                              order.status === 'completed' ? 'border-green-500/50 text-green-400' :
                              order.status === 'assigned' ? 'border-purple-500/50 text-purple-400' :
                              'border-blue-500/50 text-blue-400'
                            }>
                              {order.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Receipts Tab */}
        <TabsContent value="receipts" className="space-y-4">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ArrowDownCircle className="w-5 h-5 text-green-400" />
                Money Received (Receipts)
              </CardTitle>
              <CardDescription>Payments received from clients</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead>Trans No.</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Vehicle</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Bank</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.filter(t => t.transaction_type === 'receipt').length > 0 ? (
                    transactions.filter(t => t.transaction_type === 'receipt').map((trans) => (
                      <TableRow key={trans.id} className="border-border">
                        <TableCell className="font-mono text-primary">{trans.transaction_number}</TableCell>
                        <TableCell>{new Date(trans.transaction_date).toLocaleDateString()}</TableCell>
                        <TableCell>{trans.reference_name || '-'}</TableCell>
                        <TableCell>{trans.vehicle_number || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={getCategoryBadgeStyle(trans.category)}>
                            {getCategoryLabel(trans.category)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{trans.bank_account_name || '-'}</TableCell>
                        <TableCell className="capitalize">{trans.payment_mode?.replace('_', ' ')}</TableCell>
                        <TableCell className="text-right font-bold text-green-400">+{formatCurrency(trans.amount)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No receipts recorded yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments" className="space-y-4">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ArrowUpCircle className="w-5 h-5 text-orange-400" />
                Money Paid (Payments)
              </CardTitle>
              <CardDescription>Payments made to vendors</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead>Trans No.</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Vehicle</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Bank</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.filter(t => t.transaction_type === 'payment').length > 0 ? (
                    transactions.filter(t => t.transaction_type === 'payment').map((trans) => (
                      <TableRow key={trans.id} className="border-border">
                        <TableCell className="font-mono text-primary">{trans.transaction_number}</TableCell>
                        <TableCell>{new Date(trans.transaction_date).toLocaleDateString()}</TableCell>
                        <TableCell>{trans.reference_name || '-'}</TableCell>
                        <TableCell>{trans.vehicle_number || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={getCategoryBadgeStyle(trans.category)}>
                            {getCategoryLabel(trans.category)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{trans.bank_account_name || '-'}</TableCell>
                        <TableCell className="capitalize">{trans.payment_mode?.replace('_', ' ')}</TableCell>
                        <TableCell className="text-right font-bold text-orange-400">-{formatCurrency(trans.amount)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No payments recorded yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses" className="space-y-4">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-red-400" />
                Company Expenses
              </CardTitle>
              <CardDescription>Employee salaries, office expenses, and operational costs</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead>Trans No.</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Bank</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.filter(t => t.category?.startsWith('expense_')).length > 0 ? (
                    transactions.filter(t => t.category?.startsWith('expense_')).map((trans) => (
                      <TableRow key={trans.id} className="border-border">
                        <TableCell className="font-mono text-primary">{trans.transaction_number}</TableCell>
                        <TableCell>{new Date(trans.transaction_date).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={getCategoryBadgeStyle(trans.category)}>
                            {getCategoryLabel(trans.category)}
                          </Badge>
                        </TableCell>
                        <TableCell>{trans.description || '-'}</TableCell>
                        <TableCell className="text-sm">{trans.bank_account_name || '-'}</TableCell>
                        <TableCell className="capitalize">{trans.payment_mode?.replace('_', ' ')}</TableCell>
                        <TableCell className="text-right font-bold text-red-400">-{formatCurrency(trans.amount)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No expenses recorded yet. Click "New Transaction" and select "Expense" to add one.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bank Accounts Tab */}
        <TabsContent value="banks" className="space-y-4">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building className="w-5 h-5 text-blue-400" />
                Bank Accounts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead>Account Name</TableHead>
                    <TableHead>Bank</TableHead>
                    <TableHead>Account No.</TableHead>
                    <TableHead>IFSC</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Opening Bal.</TableHead>
                    <TableHead className="text-right">Current Balance</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bankAccounts.length > 0 ? (
                    bankAccounts.map((bank) => (
                      <TableRow key={bank.id} className="border-border">
                        <TableCell className="font-medium">{bank.account_name}</TableCell>
                        <TableCell>{bank.bank_name}</TableCell>
                        <TableCell className="font-mono">{bank.account_number}</TableCell>
                        <TableCell className="font-mono text-sm">{bank.ifsc_code || '-'}</TableCell>
                        <TableCell className="capitalize">{bank.account_type}</TableCell>
                        <TableCell>{formatCurrency(bank.opening_balance)}</TableCell>
                        <TableCell className={`text-right font-bold ${bank.current_balance >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {formatCurrency(bank.current_balance)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => {
                              setSelectedItem(bank);
                              setDeleteType("bank");
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No bank accounts added yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* All Transactions Tab */}
        <TabsContent value="all" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-4">
            <Select value={transactionFilter.type} onValueChange={(v) => setTransactionFilter({...transactionFilter, type: v})}>
              <SelectTrigger className="w-[150px] bg-secondary/50">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="receipt">Receipts</SelectItem>
                <SelectItem value="payment">Payments</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={transactionFilter.category} onValueChange={(v) => setTransactionFilter({...transactionFilter, category: v})}>
              <SelectTrigger className="w-[180px] bg-secondary/50">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="client_advance">Client Advance</SelectItem>
                <SelectItem value="client_balance">Client Balance</SelectItem>
                <SelectItem value="vendor_advance">Vendor Advance</SelectItem>
                <SelectItem value="vendor_balance">Vendor Balance</SelectItem>
                <SelectItem value="expense_salary">Employee Salary</SelectItem>
                <SelectItem value="expense_stationery">Stationery</SelectItem>
                <SelectItem value="expense_office">Office Expense</SelectItem>
                <SelectItem value="expense_fuel">Fuel Expense</SelectItem>
                <SelectItem value="expense_maintenance">Maintenance</SelectItem>
                <SelectItem value="expense_other">Other Expense</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={transactionFilter.bankAccountId} onValueChange={(v) => setTransactionFilter({...transactionFilter, bankAccountId: v})}>
              <SelectTrigger className="w-[200px] bg-secondary/50">
                <SelectValue placeholder="Bank Account" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Accounts</SelectItem>
                {bankAccounts.map((bank) => (
                  <SelectItem key={bank.id} value={bank.id}>{bank.account_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Card className="bg-card border-border">
            <CardContent className="pt-6">
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead>Trans No.</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Vehicle</TableHead>
                    <TableHead>Bank</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTransactions.length > 0 ? (
                    filteredTransactions.map((trans) => (
                      <TableRow key={trans.id} className="border-border">
                        <TableCell className="font-mono text-primary">{trans.transaction_number}</TableCell>
                        <TableCell>{new Date(trans.transaction_date).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={trans.transaction_type === 'receipt' ? 'text-green-400 border-green-500/50' : 'text-orange-400 border-orange-500/50'}>
                            {trans.transaction_type === 'receipt' ? 'Receipt' : 'Payment'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={getCategoryBadgeStyle(trans.category)}>
                            {getCategoryLabel(trans.category)}
                          </Badge>
                        </TableCell>
                        <TableCell>{trans.reference_name || '-'}</TableCell>
                        <TableCell>{trans.vehicle_number || '-'}</TableCell>
                        <TableCell className="text-sm">{trans.bank_account_name || '-'}</TableCell>
                        <TableCell className={`text-right font-bold ${trans.transaction_type === 'receipt' ? 'text-green-400' : 'text-orange-400'}`}>
                          {trans.transaction_type === 'receipt' ? '+' : '-'}{formatCurrency(trans.amount)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                              onClick={() => openEditTransaction(trans)}
                              title="Edit Transaction"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              onClick={() => {
                                setSelectedItem(trans);
                                setDeleteType("transaction");
                                setDeleteDialogOpen(true);
                              }}
                              title="Delete Transaction"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                        No transactions found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Client Ledger Tab */}
        <TabsContent value="client-ledger" className="space-y-4">
          {/* Client Ledger Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-orange-400" />
                  Total Outstanding
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-400">{formatCurrency(outstandingData.total_outstanding)}</div>
              </CardContent>
            </Card>
            
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-yellow-400" />
                  Pending UTR
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-400">{pendingUtrEntries.length}</div>
              </CardContent>
            </Card>
            
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-400" />
                  Clients with Dues
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-400">{outstandingData.clients?.length || 0}</div>
              </CardContent>
            </Card>
            
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-green-400" />
                  Total Entries
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-400">{ledgerEntries.length}</div>
              </CardContent>
            </Card>
          </div>

          {/* Pending UTR Table */}
          {pendingUtrEntries.length > 0 && (
            <Card className="bg-card border-border border-yellow-500/30">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5 text-yellow-400" />
                  Payments Pending UTR Confirmation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-border">
                      <TableHead>Entry No.</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Order/Load Slip</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingUtrEntries.map((entry) => (
                      <TableRow key={entry.id} className="border-border">
                        <TableCell className="font-mono text-primary">{entry.entry_number}</TableCell>
                        <TableCell className="font-medium">{entry.client_name}</TableCell>
                        <TableCell className="text-sm">{entry.order_number || entry.load_slip_number || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={getLedgerEntryTypeBadge(entry.entry_type)}>
                            {getLedgerEntryTypeLabel(entry.entry_type)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium text-green-400">{formatCurrency(entry.amount)}</TableCell>
                        <TableCell className="text-right">
                          <Button 
                            size="sm" 
                            onClick={() => openUtrDialog(entry)}
                            className="bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Confirm UTR
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Outstanding Balances Table */}
          <Card className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-orange-400" />
                  Clients with Outstanding Balance
                </CardTitle>
              </div>
              <Button onClick={() => setPaymentDialogOpen(true)} className="bg-primary">
                <DollarSign className="w-4 h-4 mr-2" />
                Receive Payment
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead>Client Name</TableHead>
                    <TableHead className="text-right">Total Billed</TableHead>
                    <TableHead className="text-right">Advances</TableHead>
                    <TableHead className="text-right">Balances</TableHead>
                    <TableHead className="text-right">Outstanding</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {outstandingData.clients?.length > 0 ? (
                    outstandingData.clients.map((client) => (
                      <TableRow key={client.client_id} className="border-border">
                        <TableCell className="font-medium">
                          {client.client_name}
                          {client.pending_utr_count > 0 && (
                            <Badge className="ml-2 bg-yellow-500/20 text-yellow-400 text-xs">
                              {client.pending_utr_count} pending UTR
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(client.total_receivables)}</TableCell>
                        <TableCell className="text-right text-blue-400">{formatCurrency(client.total_advances || 0)}</TableCell>
                        <TableCell className="text-right text-green-400">{formatCurrency(client.total_balances || 0)}</TableCell>
                        <TableCell className="text-right font-bold text-orange-400">{formatCurrency(client.outstanding_balance)}</TableCell>
                        <TableCell className="text-right">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setPaymentForm({ ...paymentForm, client_id: client.client_id });
                              setPaymentDialogOpen(true);
                            }}
                          >
                            <DollarSign className="w-4 h-4 mr-1" />
                            Receive
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No outstanding balances. All payments are cleared!
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* All Ledger Entries Table */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg">All Ledger Entries</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead>Entry No.</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>UTR Status</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ledgerEntries.length > 0 ? (
                    ledgerEntries.slice(0, 20).map((entry) => (
                      <TableRow key={entry.id} className="border-border">
                        <TableCell className="font-mono text-primary">{entry.entry_number}</TableCell>
                        <TableCell>{new Date(entry.transaction_date).toLocaleDateString()}</TableCell>
                        <TableCell className="font-medium">{entry.client_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={getLedgerEntryTypeBadge(entry.entry_type)}>
                            {getLedgerEntryTypeLabel(entry.entry_type)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {entry.entry_type === 'receivable' ? (
                            <span className="text-muted-foreground text-xs">N/A</span>
                          ) : entry.payment_status === 'confirmed' ? (
                            <div className="flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3 text-green-400" />
                              <span className="text-xs text-green-400">{entry.utr_number}</span>
                            </div>
                          ) : (
                            <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">Pending</Badge>
                          )}
                        </TableCell>
                        <TableCell className={`text-right font-medium ${
                          entry.entry_type === 'receivable' ? 'text-orange-400' : 'text-green-400'
                        }`}>
                          {entry.entry_type === 'receivable' ? '+' : '-'}{formatCurrency(entry.amount)}
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(entry.balance_amount)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No ledger entries yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vendor Ledger Tab */}
        <TabsContent value="vendor-ledger" className="space-y-4">
          {/* Outstanding Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2 text-xs">
                  <TrendingUp className="w-4 h-4 text-green-400" />
                  Total Freight
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-xl font-bold text-green-400">
                  {formatCurrency(vendorOutstandingData.total_freight || 0)}
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2 text-xs">
                  <ArrowUpCircle className="w-4 h-4 text-blue-400" />
                  Total Advance
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-xl font-bold text-blue-400">
                  {formatCurrency(vendorOutstandingData.total_advance_paid || 0)}
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2 text-xs">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  Total Balance Paid
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-xl font-bold text-emerald-400">
                  {formatCurrency(vendorOutstandingData.total_balance_paid || 0)}
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2 text-xs">
                  <Percent className="w-4 h-4 text-purple-400" />
                  TDS Deducted
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-xl font-bold text-purple-400">
                  {formatCurrency(vendorOutstandingData.total_tds_deducted || 0)}
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2 text-xs">
                  <AlertCircle className="w-4 h-4 text-orange-400" />
                  Outstanding (We Owe)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-xl font-bold text-orange-400">
                  {formatCurrency(vendorOutstandingData.total_outstanding || 0)}
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2 text-xs">
                  <Users className="w-4 h-4 text-cyan-400" />
                  Vendors with Balance
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-xl font-bold text-cyan-400">
                  {vendorOutstandingData.vendors?.length || 0}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Make Payment Button */}
          <div className="flex justify-end">
            <Button onClick={() => openVendorPaymentDialog()} className="bg-primary" data-testid="make-vendor-payment-btn">
              <Plus className="w-4 h-4 mr-2" />
              Make Payment to Vendor
            </Button>
          </div>

          {/* Outstanding by Vendor */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-orange-400" />
                Vendor-wise Summary
              </CardTitle>
              <CardDescription>Complete financial breakdown for each vendor</CardDescription>
            </CardHeader>
            <CardContent>
              {vendorOutstandingData.vendors?.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Vendor</TableHead>
                        <TableHead className="text-right text-green-400">Total Freight</TableHead>
                        <TableHead className="text-right text-blue-400">Advance Paid</TableHead>
                        <TableHead className="text-right text-emerald-400">Balance Paid</TableHead>
                        <TableHead className="text-right text-purple-400">TDS Deducted</TableHead>
                        <TableHead className="text-right text-orange-400">Outstanding</TableHead>
                        <TableHead className="text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vendorOutstandingData.vendors.map((vendor) => (
                        <TableRow key={vendor.vendor_id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{vendor.vendor_name}</p>
                              {vendor.pending_payments > 0 && (
                                <Badge className="bg-yellow-500/20 text-yellow-400 text-xs mt-1">
                                  {vendor.pending_payments} pending
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-semibold text-green-400">
                            {formatCurrency(vendor.total_payable || 0)}
                          </TableCell>
                          <TableCell className="text-right text-blue-400">
                            {formatCurrency(vendor.total_advance_paid || 0)}
                          </TableCell>
                          <TableCell className="text-right text-emerald-400">
                            {formatCurrency(vendor.total_balance_paid || 0)}
                          </TableCell>
                          <TableCell className="text-right text-purple-400">
                            {formatCurrency(vendor.total_tds_deducted || 0)}
                          </TableCell>
                          <TableCell className="text-right font-bold text-orange-400">
                            {formatCurrency(vendor.outstanding || 0)}
                          </TableCell>
                          <TableCell className="text-center">
                            <Button 
                              size="sm" 
                              onClick={() => openVendorPaymentDialog({ id: vendor.vendor_id, name: vendor.vendor_name }, true)}
                              data-testid={`pay-vendor-${vendor.vendor_id}`}
                              disabled={vendor.outstanding <= 0}
                              title={vendor.outstanding > 0 ? `Quick Pay ₹${vendor.outstanding.toLocaleString()}` : "No outstanding balance"}
                            >
                              <ArrowUpCircle className="w-4 h-4 mr-1" />
                              Pay {vendor.outstanding > 0 && `₹${(vendor.outstanding / 1000).toFixed(0)}K`}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Totals Row */}
                      <TableRow className="bg-secondary/30 font-bold">
                        <TableCell>TOTAL ({vendorOutstandingData.vendors.length} Vendors)</TableCell>
                        <TableCell className="text-right text-green-400">
                          {formatCurrency(vendorOutstandingData.total_freight || 0)}
                        </TableCell>
                        <TableCell className="text-right text-blue-400">
                          {formatCurrency(vendorOutstandingData.total_advance_paid || 0)}
                        </TableCell>
                        <TableCell className="text-right text-emerald-400">
                          {formatCurrency(vendorOutstandingData.total_balance_paid || 0)}
                        </TableCell>
                        <TableCell className="text-right text-purple-400">
                          {formatCurrency(vendorOutstandingData.total_tds_deducted || 0)}
                        </TableCell>
                        <TableCell className="text-right text-orange-400">
                          {formatCurrency(vendorOutstandingData.total_outstanding || 0)}
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-400 opacity-50" />
                  <p>No vendor transactions recorded</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* All Vendor Ledger Entries */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle>All Vendor Ledger Entries</CardTitle>
              <CardDescription>Complete history of vendor transactions</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-muted-foreground">Entry #</TableHead>
                    <TableHead className="text-muted-foreground">Date</TableHead>
                    <TableHead className="text-muted-foreground">Vendor</TableHead>
                    <TableHead className="text-muted-foreground">Type</TableHead>
                    <TableHead className="text-muted-foreground">Vehicle</TableHead>
                    <TableHead className="text-muted-foreground text-right">Amount</TableHead>
                    <TableHead className="text-muted-foreground">Status</TableHead>
                    <TableHead className="text-muted-foreground">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vendorLedgerEntries.length > 0 ? (
                    vendorLedgerEntries.map((entry) => (
                      <TableRow key={entry.id} className="border-border">
                        <TableCell className="font-mono text-sm">{entry.entry_number}</TableCell>
                        <TableCell>{entry.transaction_date}</TableCell>
                        <TableCell className="font-medium">{entry.vendor_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={getVendorLedgerEntryTypeBadge(entry.entry_type)}>
                            {getVendorLedgerEntryTypeLabel(entry.entry_type)}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{entry.vehicle_number || '-'}</TableCell>
                        <TableCell className={`text-right font-medium ${entry.entry_type === 'payable' ? 'text-orange-400' : 'text-green-400'}`}>
                          {entry.entry_type === 'payable' ? '+' : '-'}{formatCurrency(entry.amount)}
                        </TableCell>
                        <TableCell>
                          {entry.entry_type !== 'payable' && (
                            <Badge variant="outline" className={entry.payment_status === 'confirmed' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                              {entry.payment_status === 'confirmed' ? 'Confirmed' : 'Pending UTR'}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {entry.entry_type !== 'payable' && entry.payment_status === 'pending' && (
                            <Button size="sm" variant="outline" onClick={() => openVendorUtrDialog(entry)} data-testid={`confirm-vendor-utr-${entry.id}`}>
                              Confirm UTR
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No vendor ledger entries yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vendor Accounts Tab */}
        <TabsContent value="vendor-accounts" className="space-y-4">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-primary" />
                Vendor Bank Accounts
              </CardTitle>
              <CardDescription>View all vendor bank account details for payments</CardDescription>
            </CardHeader>
            <CardContent>
              {vendors.filter(v => v.bank_accounts && v.bank_accounts.length > 0).length > 0 ? (
                <div className="space-y-4">
                  {vendors.filter(v => v.bank_accounts && v.bank_accounts.length > 0).map((vendor) => (
                    <Card key={vendor.id} className="bg-secondary/30 border-border/50">
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="text-base">{vendor.name}</CardTitle>
                            <CardDescription className="text-xs">
                              {vendor.contact_person} | {vendor.phone}
                            </CardDescription>
                          </div>
                          <Badge variant="outline" className="border-primary/50 text-primary">
                            {vendor.bank_accounts.length} Account{vendor.bank_accounts.length > 1 ? 's' : ''}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-2">
                        <div className="grid gap-3">
                          {vendor.bank_accounts.map((account, idx) => (
                            <div key={idx} className="p-3 bg-secondary/50 rounded-lg border border-border/30" data-testid={`vendor-account-${vendor.id}-${idx}`}>
                              <div className="flex items-start justify-between">
                                <div className="space-y-1">
                                  <p className="font-medium text-sm">{account.account_name || 'N/A'}</p>
                                  <p className="text-muted-foreground text-xs">{account.bank_name}</p>
                                </div>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs"
                                  onClick={() => {
                                    setTransactionForm({
                                      ...transactionForm,
                                      transaction_type: "payment",
                                      category: "vendor_balance",
                                      reference_type: "vendor",
                                      reference_id: vendor.id,
                                      description: `Payment to ${vendor.name} - A/C: ${account.account_number}`
                                    });
                                    setTransactionDialogOpen(true);
                                  }}
                                  data-testid={`pay-vendor-${vendor.id}-${idx}`}
                                >
                                  <ArrowUpCircle className="w-3 h-3 mr-1" />
                                  Pay
                                </Button>
                              </div>
                              <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                                <div>
                                  <span className="text-muted-foreground">A/C Number:</span>
                                  <span className="ml-1 font-mono">{account.account_number}</span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">IFSC:</span>
                                  <span className="ml-1 font-mono">{account.ifsc_code}</span>
                                </div>
                                {account.branch && (
                                  <div className="col-span-2">
                                    <span className="text-muted-foreground">Branch:</span>
                                    <span className="ml-1">{account.branch}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Truck className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No vendor bank accounts found</p>
                  <p className="text-xs mt-1">Add bank accounts to vendors in the Vendors page</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TDS Report Tab */}
        <TabsContent value="tds-report" className="space-y-4">
          {/* TDS Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-green-500/20 to-green-600/10 border-green-500/30">
              <CardHeader className="pb-2">
                <CardDescription className="text-green-300 flex items-center gap-2">
                  <ArrowDownCircle className="w-4 h-4" />
                  TDS Receivable (From Clients)
                </CardDescription>
                <CardTitle className="text-2xl text-green-400">
                  {formatCurrency(tdsReportData.summary.total_tds_receivable || 0)}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">Deducted by clients - claim from Govt</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border-purple-500/30">
              <CardHeader className="pb-2">
                <CardDescription className="text-purple-300 flex items-center gap-2">
                  <ArrowUpCircle className="w-4 h-4" />
                  TDS Payable (To Govt)
                </CardDescription>
                <CardTitle className="text-2xl text-purple-400">
                  {formatCurrency(tdsReportData.summary.total_tds_payable || 0)}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">Deducted from vendors - deposit to Govt</p>
              </CardContent>
            </Card>
            
            <Card className={`bg-gradient-to-br ${tdsReportData.summary.net_tds >= 0 ? 'from-orange-500/20 to-orange-600/10 border-orange-500/30' : 'from-emerald-500/20 to-emerald-600/10 border-emerald-500/30'}`}>
              <CardHeader className="pb-2">
                <CardDescription className={tdsReportData.summary.net_tds >= 0 ? 'text-orange-300' : 'text-emerald-300'}>
                  Net TDS {tdsReportData.summary.net_tds >= 0 ? 'Payable' : 'Receivable'}
                </CardDescription>
                <CardTitle className={`text-2xl ${tdsReportData.summary.net_tds >= 0 ? 'text-orange-400' : 'text-emerald-400'}`}>
                  {formatCurrency(Math.abs(tdsReportData.summary.net_tds || 0))}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">
                  {tdsReportData.summary.net_tds >= 0 ? 'Amount to deposit to Government' : 'Amount to claim from Government'}
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-400" />
                  Total TDS Entries
                </CardDescription>
                <CardTitle className="text-2xl text-blue-400">
                  {tdsReportData.total_entries || 0}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">
                  Clients: {tdsReportData.client_tds_entries?.length || 0} | Vendors: {tdsReportData.vendor_tds_entries?.length || 0}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* TDS Rate Breakdown */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Percent className="w-5 h-5 text-purple-400" />
                TDS Breakdown by Rate
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                  <h4 className="font-semibold text-green-400 mb-3">Client TDS (Receivable)</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">TDS @ 1%</span>
                      <span className="text-green-400 font-semibold">{formatCurrency(tdsReportData.summary.client_tds_1_percent || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">TDS @ 2%</span>
                      <span className="text-green-400 font-semibold">{formatCurrency(tdsReportData.summary.client_tds_2_percent || 0)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-green-500/30">
                      <span className="font-semibold">Total</span>
                      <span className="text-green-400 font-bold">{formatCurrency(tdsReportData.summary.total_tds_receivable || 0)}</span>
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/30">
                  <h4 className="font-semibold text-purple-400 mb-3">Vendor TDS (Payable)</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">TDS @ 1%</span>
                      <span className="text-purple-400 font-semibold">{formatCurrency(tdsReportData.summary.vendor_tds_1_percent || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">TDS @ 2%</span>
                      <span className="text-purple-400 font-semibold">{formatCurrency(tdsReportData.summary.vendor_tds_2_percent || 0)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-purple-500/30">
                      <span className="font-semibold">Total</span>
                      <span className="text-purple-400 font-bold">{formatCurrency(tdsReportData.summary.total_tds_payable || 0)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Date Filter and Actions */}
          <Card className="bg-card border-border">
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    TDS Report Period
                  </CardTitle>
                  <CardDescription>Filter TDS transactions by date range</CardDescription>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleTdsPeriodChange('month')}>This Month</Button>
                  <Button variant="outline" size="sm" onClick={() => handleTdsPeriodChange('last_month')}>Last Month</Button>
                  <Button variant="outline" size="sm" onClick={() => handleTdsPeriodChange('quarter')}>This Quarter</Button>
                  <Button variant="outline" size="sm" onClick={() => handleTdsPeriodChange('financial_year')}>FY {new Date().getMonth() >= 3 ? new Date().getFullYear() : new Date().getFullYear() - 1}-{(new Date().getMonth() >= 3 ? new Date().getFullYear() : new Date().getFullYear() - 1) + 1}</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row gap-4 items-end">
                <div className="grid grid-cols-2 gap-4 flex-1">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={tdsDateFilter.startDate}
                      onChange={(e) => setTdsDateFilter({...tdsDateFilter, startDate: e.target.value})}
                      className="bg-secondary/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={tdsDateFilter.endDate}
                      onChange={(e) => setTdsDateFilter({...tdsDateFilter, endDate: e.target.value})}
                      className="bg-secondary/50"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={applyTdsDateFilter} className="bg-primary">
                    <Filter className="w-4 h-4 mr-2" />
                    Apply
                  </Button>
                  <Button variant="outline" onClick={exportTdsToCSV}>
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                  <Button variant="outline" onClick={() => setTdsEmailDialogOpen(true)}>
                    <Mail className="w-4 h-4 mr-2" />
                    Email Report
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Client TDS Entries */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-400">
                <ArrowDownCircle className="w-5 h-5" />
                TDS Deducted by Clients (Receivable)
              </CardTitle>
              <CardDescription>TDS deducted from payments received from clients</CardDescription>
            </CardHeader>
            <CardContent>
              {tdsReportData.client_tds_entries?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Entry No</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead className="text-right">Gross Amount</TableHead>
                      <TableHead className="text-center">TDS Rate</TableHead>
                      <TableHead className="text-right text-green-400">TDS Amount</TableHead>
                      <TableHead className="text-right">Net Received</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tdsReportData.client_tds_entries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>{new Date(entry.transaction_date).toLocaleDateString()}</TableCell>
                        <TableCell className="font-mono text-sm">{entry.entry_number}</TableCell>
                        <TableCell>{entry.client_name}</TableCell>
                        <TableCell className="text-right">{formatCurrency(entry.amount)}</TableCell>
                        <TableCell className="text-center">
                          <Badge className="bg-green-500/20 text-green-400">{entry.tds_rate}%</Badge>
                        </TableCell>
                        <TableCell className="text-right font-semibold text-green-400">{formatCurrency(entry.tds_amount)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(entry.net_amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No TDS entries from clients for this period</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Vendor TDS Entries */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-purple-400">
                <ArrowUpCircle className="w-5 h-5" />
                TDS Deducted from Vendors (Payable)
              </CardTitle>
              <CardDescription>TDS deducted from payments made to vendors</CardDescription>
            </CardHeader>
            <CardContent>
              {tdsReportData.vendor_tds_entries?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Entry No</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead className="text-right">Gross Amount</TableHead>
                      <TableHead className="text-center">TDS Rate</TableHead>
                      <TableHead className="text-right text-purple-400">TDS Amount</TableHead>
                      <TableHead className="text-right">Net Paid</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tdsReportData.vendor_tds_entries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>{new Date(entry.transaction_date).toLocaleDateString()}</TableCell>
                        <TableCell className="font-mono text-sm">{entry.entry_number}</TableCell>
                        <TableCell>{entry.vendor_name}</TableCell>
                        <TableCell className="text-right">{formatCurrency(entry.amount)}</TableCell>
                        <TableCell className="text-center">
                          <Badge className="bg-purple-500/20 text-purple-400">{entry.tds_rate}%</Badge>
                        </TableCell>
                        <TableCell className="text-right font-semibold text-purple-400">{formatCurrency(entry.tds_amount)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(entry.net_amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No TDS entries for vendors for this period</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bank Transfers Tab - UCO Bank Manual Transfer Queue */}
        <TabsContent value="bank-transfers" className="space-y-4">
          {/* Header with Actions */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Send className="w-5 h-5 text-blue-400" />
                UCO Bank Transfer Queue
              </h2>
              <p className="text-sm text-muted-foreground">
                Pending vendor payments to transfer via UCO Bank Corporate mBanking
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={fetchPendingTransfers}>
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={exportPendingTransfers} disabled={pendingTransfers.length === 0}>
                <Download className="w-4 h-4 mr-1" />
                Export CSV
              </Button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-card border-border">
              <CardContent className="pt-4">
                <div className="text-sm text-muted-foreground">Total Pending</div>
                <div className="text-2xl font-bold text-orange-400">{pendingTransfers.length}</div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="pt-4">
                <div className="text-sm text-muted-foreground">Total Amount</div>
                <div className="text-2xl font-bold text-primary">
                  {formatCurrency(pendingTransfers.reduce((sum, t) => sum + t.outstanding, 0))}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="pt-4">
                <div className="text-sm text-muted-foreground">With Bank Details</div>
                <div className="text-2xl font-bold text-green-400">
                  {pendingTransfers.filter(t => t.account_number).length}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="pt-4">
                <div className="text-sm text-muted-foreground">Missing Bank Info</div>
                <div className="text-2xl font-bold text-red-400">
                  {pendingTransfers.filter(t => !t.account_number).length}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Instructions Card */}
          <Card className="bg-blue-500/10 border-blue-500/30">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-blue-400 mb-1">How to use UCO Bank Transfer Queue:</p>
                  <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                    <li>Click <strong>"Copy All"</strong> to copy vendor bank details to clipboard</li>
                    <li>Open <strong>UCO Bank Corporate mBanking</strong> app or portal</li>
                    <li>Add beneficiary and initiate NEFT/RTGS/IMPS transfer</li>
                    <li>After transfer, click <strong>"Mark Done"</strong> and enter the UTR number</li>
                  </ol>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pending Transfers Table */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg">Pending Vendor Payments</CardTitle>
              <CardDescription>Click on a row to copy bank details for UCO Bank transfer</CardDescription>
            </CardHeader>
            <CardContent>
              {pendingTransfers.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Vendor</TableHead>
                        <TableHead>Bank Details</TableHead>
                        <TableHead className="text-right">Total Freight</TableHead>
                        <TableHead className="text-right">Paid</TableHead>
                        <TableHead className="text-right">Outstanding</TableHead>
                        <TableHead className="text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingTransfers.map((transfer, idx) => (
                        <TableRow key={idx} className="hover:bg-secondary/30">
                          <TableCell>
                            <div>
                              <p className="font-medium">{transfer.vendor_name}</p>
                              {transfer.phone && (
                                <p className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  {transfer.phone}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {transfer.account_number ? (
                              <div className="text-sm space-y-0.5">
                                <p className="font-mono">{transfer.account_number}</p>
                                <p className="text-xs text-muted-foreground">
                                  {transfer.ifsc_code} • {transfer.bank_name}
                                </p>
                                <p className="text-xs text-muted-foreground">{transfer.account_holder}</p>
                              </div>
                            ) : (
                              <Badge variant="destructive" className="text-xs">No Bank Info</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(transfer.total_freight)}
                          </TableCell>
                          <TableCell className="text-right text-green-400">
                            {formatCurrency(transfer.advance_paid + transfer.balance_paid)}
                          </TableCell>
                          <TableCell className="text-right font-bold text-orange-400">
                            {formatCurrency(transfer.outstanding)}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              {transfer.account_number && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-8 text-xs"
                                    onClick={() => copyToClipboard(transfer.account_number, "Account No")}
                                    title="Copy Account Number"
                                  >
                                    {copiedField === "Account No" ? <CheckCheck className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-8 text-xs bg-blue-500/20 text-blue-400 border-blue-500/30"
                                    onClick={() => copyAllBankDetails(transfer)}
                                    title="Copy All Bank Details"
                                  >
                                    Copy All
                                  </Button>
                                </>
                              )}
                              <Button
                                size="sm"
                                className="h-8 text-xs bg-green-600 hover:bg-green-700"
                                onClick={() => {
                                  setSelectedTransfer(transfer);
                                  setMarkTransferDialogOpen(true);
                                }}
                                title="Mark as Transferred"
                              >
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Mark Done
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  
                  {/* Total Row */}
                  <div className="flex justify-between items-center mt-4 p-3 bg-secondary/30 rounded-lg">
                    <span className="font-medium">Total Pending Transfers:</span>
                    <span className="text-xl font-bold text-primary">
                      {formatCurrency(pendingTransfers.reduce((sum, t) => sum + t.outstanding, 0))}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-400 opacity-50" />
                  <p className="text-lg font-medium">All payments are up to date!</p>
                  <p className="text-sm">No pending vendor payments to transfer</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Bank Account Dialog */}
      <Dialog open={bankDialogOpen} onOpenChange={setBankDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add Bank Account</DialogTitle>
            <DialogDescription>Add a new bank account for transactions</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleBankSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Account Name</Label>
                <Input
                  value={bankForm.account_name}
                  onChange={(e) => setBankForm({...bankForm, account_name: e.target.value})}
                  placeholder="Main Account"
                  className="bg-secondary/50"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Bank Name</Label>
                <Input
                  value={bankForm.bank_name}
                  onChange={(e) => setBankForm({...bankForm, bank_name: e.target.value})}
                  placeholder="State Bank of India"
                  className="bg-secondary/50"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Account Number</Label>
                <Input
                  value={bankForm.account_number}
                  onChange={(e) => setBankForm({...bankForm, account_number: e.target.value})}
                  placeholder="1234567890"
                  className="bg-secondary/50"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>IFSC Code</Label>
                <Input
                  value={bankForm.ifsc_code}
                  onChange={(e) => setBankForm({...bankForm, ifsc_code: e.target.value})}
                  placeholder="SBIN0001234"
                  className="bg-secondary/50"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Branch</Label>
                <Input
                  value={bankForm.branch}
                  onChange={(e) => setBankForm({...bankForm, branch: e.target.value})}
                  placeholder="Main Branch"
                  className="bg-secondary/50"
                />
              </div>
              <div className="space-y-2">
                <Label>Account Type</Label>
                <Select value={bankForm.account_type} onValueChange={(v) => setBankForm({...bankForm, account_type: v})}>
                  <SelectTrigger className="bg-secondary/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="current">Current</SelectItem>
                    <SelectItem value="savings">Savings</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Opening Balance</Label>
              <Input
                type="number"
                value={bankForm.opening_balance}
                onChange={(e) => setBankForm({...bankForm, opening_balance: e.target.value})}
                placeholder="0"
                className="bg-secondary/50"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setBankDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-primary">Add Account</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Transaction Dialog */}
      <Dialog open={transactionDialogOpen} onOpenChange={setTransactionDialogOpen}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">New Transaction</DialogTitle>
            <DialogDescription>Record a payment receipt, vendor payment, or expense</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleTransactionSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Transaction Type</Label>
                <Select 
                  value={transactionForm.transaction_type} 
                  onValueChange={(v) => {
                    let defaultCategory = 'client_advance';
                    let refType = 'client';
                    if (v === 'payment') {
                      defaultCategory = 'vendor_advance';
                      refType = 'vendor';
                    } else if (v === 'expense') {
                      defaultCategory = 'expense_salary';
                      refType = '';
                    }
                    setTransactionForm({
                      ...transactionForm, 
                      transaction_type: v,
                      category: defaultCategory,
                      reference_type: refType,
                      reference_id: ''
                    });
                  }}
                >
                  <SelectTrigger className="bg-secondary/50" data-testid="transaction-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="receipt">
                      <div className="flex items-center gap-2">
                        <ArrowDownCircle className="w-4 h-4 text-green-400" />
                        Receipt (Money In)
                      </div>
                    </SelectItem>
                    <SelectItem value="payment">
                      <div className="flex items-center gap-2">
                        <ArrowUpCircle className="w-4 h-4 text-orange-400" />
                        Payment (Money Out)
                      </div>
                    </SelectItem>
                    <SelectItem value="expense">
                      <div className="flex items-center gap-2">
                        <TrendingDown className="w-4 h-4 text-red-400" />
                        Expense (Company Cost)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={transactionForm.category} onValueChange={(v) => setTransactionForm({...transactionForm, category: v})} data-testid="category-select">
                  <SelectTrigger className="bg-secondary/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {transactionForm.transaction_type === 'receipt' ? (
                      <>
                        <SelectItem value="client_advance">Client Advance</SelectItem>
                        <SelectItem value="client_balance">Client Balance</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </>
                    ) : transactionForm.transaction_type === 'expense' ? (
                      <>
                        <SelectItem value="expense_salary">Employee Salary</SelectItem>
                        <SelectItem value="expense_stationery">Stationery</SelectItem>
                        <SelectItem value="expense_office">Office Expense</SelectItem>
                        <SelectItem value="expense_fuel">Fuel Expense</SelectItem>
                        <SelectItem value="expense_maintenance">Maintenance</SelectItem>
                        <SelectItem value="expense_other">Other Expense</SelectItem>
                      </>
                    ) : (
                      <>
                        <SelectItem value="vendor_advance">Vendor Advance</SelectItem>
                        <SelectItem value="vendor_balance">Vendor Balance</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount (₹)</Label>
                <Input
                  type="number"
                  value={transactionForm.amount}
                  onChange={(e) => setTransactionForm({...transactionForm, amount: e.target.value})}
                  placeholder="10000"
                  className="bg-secondary/50"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={transactionForm.transaction_date}
                  onChange={(e) => setTransactionForm({...transactionForm, transaction_date: e.target.value})}
                  className="bg-secondary/50"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Bank Account</Label>
              <Select value={transactionForm.bank_account_id} onValueChange={(v) => setTransactionForm({...transactionForm, bank_account_id: v})}>
                <SelectTrigger className="bg-secondary/50">
                  <SelectValue placeholder="Select bank account" />
                </SelectTrigger>
                <SelectContent>
                  {bankAccounts.map((bank) => (
                    <SelectItem key={bank.id} value={bank.id}>
                      {bank.account_name} ({bank.bank_name})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {transactionForm.transaction_type !== 'expense' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{transactionForm.transaction_type === 'receipt' ? 'Client' : 'Vendor'}</Label>
                  <Select 
                    value={transactionForm.reference_id} 
                    onValueChange={(v) => {
                      const selectedVendor = vendors.find(vendor => vendor.id === v);
                      let newDescription = transactionForm.description;
                      if (transactionForm.transaction_type === 'payment' && selectedVendor?.bank_accounts?.length > 0) {
                        const firstAccount = selectedVendor.bank_accounts[0];
                        newDescription = `Payment to ${selectedVendor.name} - A/C: ${firstAccount.account_number} (${firstAccount.bank_name}, IFSC: ${firstAccount.ifsc_code})`;
                      }
                      setTransactionForm({...transactionForm, reference_id: v, description: newDescription});
                    }}
                  >
                    <SelectTrigger className="bg-secondary/50">
                      <SelectValue placeholder={`Select ${transactionForm.transaction_type === 'receipt' ? 'client' : 'vendor'}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {transactionForm.transaction_type === 'receipt' ? (
                        clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>{client.company_name}</SelectItem>
                        ))
                      ) : (
                        vendors.map((vendor) => (
                          <SelectItem key={vendor.id} value={vendor.id}>
                            <div className="flex items-center gap-2">
                              <span>{vendor.name}</span>
                              {vendor.bank_accounts?.length > 0 && (
                                <Badge variant="outline" className="text-xs h-5 px-1">{vendor.bank_accounts.length} A/C</Badge>
                              )}
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Vehicle Number</Label>
                  <Input
                    value={transactionForm.vehicle_number}
                    onChange={(e) => setTransactionForm({...transactionForm, vehicle_number: e.target.value.toUpperCase()})}
                    placeholder="AS01AB1234"
                    className="bg-secondary/50"
                  />
                </div>
              </div>
            )}

            {/* Show vendor bank account details when vendor is selected for payment */}
            {transactionForm.transaction_type === 'payment' && transactionForm.reference_id && (
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <h4 className="text-sm font-medium text-blue-400 mb-2 flex items-center gap-2">
                  <Building className="w-4 h-4" />
                  Vendor Bank Details
                </h4>
                {(() => {
                  const selectedVendor = vendors.find(v => v.id === transactionForm.reference_id);
                  if (!selectedVendor?.bank_accounts?.length) {
                    return <p className="text-xs text-muted-foreground">No bank accounts found for this vendor</p>;
                  }
                  return (
                    <div className="space-y-2">
                      {selectedVendor.bank_accounts.map((account, idx) => (
                        <div key={idx} className="text-xs bg-secondary/30 p-2 rounded">
                          <div className="font-medium">{account.account_name || 'N/A'} - {account.bank_name}</div>
                          <div className="text-muted-foreground">
                            A/C: {account.account_number} | IFSC: {account.ifsc_code}
                            {account.branch && ` | Branch: ${account.branch}`}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}

            <div className="space-y-2">
              <Label>Payment Mode</Label>
              <Select value={transactionForm.payment_mode} onValueChange={(v) => setTransactionForm({...transactionForm, payment_mode: v})}>
                <SelectTrigger className="bg-secondary/50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Input
                value={transactionForm.description}
                onChange={(e) => setTransactionForm({...transactionForm, description: e.target.value})}
                placeholder="Payment for load slip #..."
                className="bg-secondary/50"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTransactionDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-primary">Record Transaction</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this {deleteType === 'bank' ? 'bank account' : 'transaction'}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receive Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Receive Payment</DialogTitle>
            <DialogDescription>Record advance or balance payment from client</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleReceivePayment} className="space-y-4">
            <div className="space-y-2">
              <Label>Client</Label>
              <Select value={paymentForm.client_id} onValueChange={(v) => setPaymentForm({...paymentForm, client_id: v})}>
                <SelectTrigger className="bg-secondary/50">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>{client.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Payment Type</Label>
                <Select value={paymentForm.payment_type} onValueChange={(v) => setPaymentForm({...paymentForm, payment_type: v})}>
                  <SelectTrigger className="bg-secondary/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="advance">Advance</SelectItem>
                    <SelectItem value="balance">Balance</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Amount (₹)</Label>
                <Input
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                  placeholder="50000"
                  className="bg-secondary/50"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Payment Mode</Label>
                <Select value={paymentForm.payment_mode} onValueChange={(v) => setPaymentForm({...paymentForm, payment_mode: v})}>
                  <SelectTrigger className="bg-secondary/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>UTR Number</Label>
                <Input
                  value={paymentForm.utr_number}
                  onChange={(e) => setPaymentForm({...paymentForm, utr_number: e.target.value})}
                  placeholder="UTR123456789"
                  className="bg-secondary/50"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Deposit to Account (Optional)</Label>
              <Select value={paymentForm.bank_account_id || "none"} onValueChange={(v) => setPaymentForm({...paymentForm, bank_account_id: v === "none" ? "" : v})}>
                <SelectTrigger className="bg-secondary/50">
                  <SelectValue placeholder="Select bank account" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {bankAccounts.map((bank) => (
                    <SelectItem key={bank.id} value={bank.id}>{bank.account_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* TDS Deduction Section */}
            <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-purple-300 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={paymentForm.tds_applicable}
                    onChange={(e) => setPaymentForm({...paymentForm, tds_applicable: e.target.checked, tds_rate: e.target.checked ? "1" : ""})}
                    className="rounded border-purple-500"
                  />
                  TDS Deducted by Client
                </Label>
              </div>
              {paymentForm.tds_applicable && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-sm">TDS Rate</Label>
                    <Select value={paymentForm.tds_rate} onValueChange={(v) => setPaymentForm({...paymentForm, tds_rate: v})}>
                      <SelectTrigger className="bg-secondary/50">
                        <SelectValue placeholder="Select rate" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1%</SelectItem>
                        <SelectItem value="2">2%</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">TDS Amount</Label>
                    <div className="p-2 bg-purple-500/20 rounded text-purple-300 font-semibold">
                      ₹{paymentForm.amount && paymentForm.tds_rate ? (parseFloat(paymentForm.amount) * parseFloat(paymentForm.tds_rate) / 100).toLocaleString() : 0}
                    </div>
                  </div>
                </div>
              )}
              {paymentForm.tds_applicable && paymentForm.amount && paymentForm.tds_rate && (
                <div className="text-sm text-muted-foreground">
                  Net Amount Received: <span className="text-green-400 font-semibold">
                    ₹{(parseFloat(paymentForm.amount) - (parseFloat(paymentForm.amount) * parseFloat(paymentForm.tds_rate) / 100)).toLocaleString()}
                  </span>
                </div>
              )}
            </div>

            {!paymentForm.utr_number && (
              <div className="p-2 bg-yellow-500/10 border border-yellow-500/30 rounded text-xs text-yellow-400">
                Without UTR, payment will be marked as "Pending Confirmation"
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-primary">Record Payment</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* UTR Confirmation Dialog */}
      <Dialog open={utrDialogOpen} onOpenChange={setUtrDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Confirm UTR Details</DialogTitle>
            <DialogDescription>
              Entry: {selectedLedgerEntry?.entry_number} - {formatCurrency(selectedLedgerEntry?.amount || 0)}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleConfirmUtr} className="space-y-4">
            <div className="space-y-2">
              <Label>UTR Number *</Label>
              <Input
                value={utrForm.utr_number}
                onChange={(e) => setUtrForm({...utrForm, utr_number: e.target.value})}
                placeholder="UTR123456789"
                className="bg-secondary/50"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Payment Mode</Label>
                <Select value={utrForm.payment_mode} onValueChange={(v) => setUtrForm({...utrForm, payment_mode: v})}>
                  <SelectTrigger className="bg-secondary/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Bank Name</Label>
                <Input
                  value={utrForm.bank_name}
                  onChange={(e) => setUtrForm({...utrForm, bank_name: e.target.value})}
                  placeholder="HDFC Bank"
                  className="bg-secondary/50"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setUtrDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700">
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Confirm UTR
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Vendor Payment Dialog */}
      <Dialog open={vendorPaymentDialogOpen} onOpenChange={setVendorPaymentDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowUpCircle className="w-5 h-5 text-orange-400" />
              Make Payment to Vendor
            </DialogTitle>
            <DialogDescription>Record a payment made to a vendor</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleVendorPayment} className="space-y-4">
            <div className="space-y-2">
              <Label>Vendor *</Label>
              <Select value={vendorPaymentForm.vendor_id} onValueChange={(v) => {
                const vendor = vendors.find(ve => ve.id === v);
                // Also find vendor financial data from outstanding
                const vendorData = vendorOutstandingData.vendors?.find(vd => vd.vendor_id === v);
                setVendorPaymentForm({
                  ...vendorPaymentForm,
                  vendor_id: v,
                  description: `Payment to ${vendor?.name || ''}`,
                  total_freight: vendorData?.total_payable || 0,
                  total_advance: vendorData?.total_advance_paid || 0,
                  outstanding: vendorData?.outstanding || 0
                });
              }}>
                <SelectTrigger className="bg-secondary/50">
                  <SelectValue placeholder="Select vendor" />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map((vendor) => (
                    <SelectItem key={vendor.id} value={vendor.id}>
                      <div className="flex items-center gap-2">
                        {vendor.name}
                        {vendor.bank_accounts?.length > 0 && (
                          <Badge variant="outline" className="text-xs h-5">{vendor.bank_accounts.length} A/C</Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Show vendor financial summary when vendor is selected */}
            {vendorPaymentForm.vendor_id && (vendorPaymentForm.total_freight > 0 || vendorPaymentForm.outstanding > 0) && (
              <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg" data-testid="vendor-payment-summary">
                <h4 className="text-sm font-medium text-green-400 mb-2">Vendor Payment Summary</h4>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div className="bg-secondary/30 p-2 rounded text-center">
                    <div className="text-muted-foreground text-xs">Total Freight</div>
                    <div className="font-semibold text-green-400">{formatCurrency(vendorPaymentForm.total_freight)}</div>
                  </div>
                  <div className="bg-secondary/30 p-2 rounded text-center">
                    <div className="text-muted-foreground text-xs">Advance Paid</div>
                    <div className="font-semibold text-blue-400">{formatCurrency(vendorPaymentForm.total_advance)}</div>
                  </div>
                  <div className="bg-secondary/30 p-2 rounded text-center">
                    <div className="text-muted-foreground text-xs">Balance Due</div>
                    <div className="font-bold text-orange-400">{formatCurrency(vendorPaymentForm.outstanding)}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Show vendor bank details */}
            {vendorPaymentForm.vendor_id && (() => {
              const vendor = vendors.find(v => v.id === vendorPaymentForm.vendor_id);
              if (vendor?.bank_accounts?.length > 0) {
                return (
                  <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <h4 className="text-sm font-medium text-blue-400 mb-2">Vendor Bank Details</h4>
                    <div className="space-y-1 text-xs">
                      {vendor.bank_accounts.map((acc, idx) => (
                        <div key={idx} className="bg-secondary/30 p-2 rounded">
                          <div className="font-medium">{acc.account_name || acc.bank_name}</div>
                          <div className="text-muted-foreground">A/C: {acc.account_number} | IFSC: {acc.ifsc_code}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }
              return null;
            })()}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount *</Label>
                <Input
                  type="number"
                  value={vendorPaymentForm.amount}
                  onChange={(e) => setVendorPaymentForm({ ...vendorPaymentForm, amount: e.target.value })}
                  placeholder="0.00"
                  className="bg-secondary/50"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Payment Mode *</Label>
                <Select value={vendorPaymentForm.payment_mode} onValueChange={(v) => setVendorPaymentForm({ ...vendorPaymentForm, payment_mode: v })}>
                  <SelectTrigger className="bg-secondary/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>From Bank Account *</Label>
              <Select value={vendorPaymentForm.bank_account_id} onValueChange={(v) => setVendorPaymentForm({ ...vendorPaymentForm, bank_account_id: v })}>
                <SelectTrigger className="bg-secondary/50">
                  <SelectValue placeholder="Select bank account" />
                </SelectTrigger>
                <SelectContent>
                  {bankAccounts.map((bank) => (
                    <SelectItem key={bank.id} value={bank.id}>
                      {bank.account_name} - {formatCurrency(bank.current_balance)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>UTR Number</Label>
                <Input
                  value={vendorPaymentForm.utr_number}
                  onChange={(e) => setVendorPaymentForm({ ...vendorPaymentForm, utr_number: e.target.value })}
                  placeholder="Optional - Add later"
                  className="bg-secondary/50"
                />
              </div>
              <div className="space-y-2">
                <Label>Vehicle Number</Label>
                <Input
                  value={vendorPaymentForm.vehicle_number}
                  onChange={(e) => setVendorPaymentForm({ ...vendorPaymentForm, vehicle_number: e.target.value.toUpperCase() })}
                  placeholder="Optional"
                  className="bg-secondary/50"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={vendorPaymentForm.description}
                onChange={(e) => setVendorPaymentForm({ ...vendorPaymentForm, description: e.target.value })}
                placeholder="Payment details"
                className="bg-secondary/50"
              />
            </div>

            {/* TDS Deduction Section for Vendor Payment */}
            <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-orange-300 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={vendorPaymentForm.tds_applicable}
                    onChange={(e) => setVendorPaymentForm({...vendorPaymentForm, tds_applicable: e.target.checked, tds_rate: e.target.checked ? "1" : ""})}
                    className="rounded border-orange-500"
                  />
                  Deduct TDS from Vendor Payment
                </Label>
              </div>
              {vendorPaymentForm.tds_applicable && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-sm">TDS Rate</Label>
                    <Select value={vendorPaymentForm.tds_rate} onValueChange={(v) => setVendorPaymentForm({...vendorPaymentForm, tds_rate: v})}>
                      <SelectTrigger className="bg-secondary/50">
                        <SelectValue placeholder="Select rate" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1%</SelectItem>
                        <SelectItem value="2">2%</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">TDS Amount</Label>
                    <div className="p-2 bg-orange-500/20 rounded text-orange-300 font-semibold">
                      ₹{vendorPaymentForm.amount && vendorPaymentForm.tds_rate ? (parseFloat(vendorPaymentForm.amount) * parseFloat(vendorPaymentForm.tds_rate) / 100).toLocaleString() : 0}
                    </div>
                  </div>
                </div>
              )}
              {vendorPaymentForm.tds_applicable && vendorPaymentForm.amount && vendorPaymentForm.tds_rate && (
                <div className="text-sm text-muted-foreground">
                  Net Amount to Pay: <span className="text-orange-400 font-semibold">
                    ₹{(parseFloat(vendorPaymentForm.amount) - (parseFloat(vendorPaymentForm.amount) * parseFloat(vendorPaymentForm.tds_rate) / 100)).toLocaleString()}
                  </span>
                  <span className="block text-xs mt-1">TDS will be deposited to Government account</span>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setVendorPaymentDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-primary">
                <ArrowUpCircle className="w-4 h-4 mr-2" />
                Record Payment
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Vendor UTR Confirmation Dialog */}
      <Dialog open={vendorUtrDialogOpen} onOpenChange={setVendorUtrDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
              Confirm Vendor Payment UTR
            </DialogTitle>
            <DialogDescription>
              {selectedVendorEntry && (
                <span>
                  Confirm UTR for payment of {formatCurrency(selectedVendorEntry.amount)} to {selectedVendorEntry.vendor_name}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleVendorUtrConfirm} className="space-y-4">
            <div className="space-y-2">
              <Label>UTR / Transaction Reference *</Label>
              <Input
                value={vendorPaymentForm.utr_number}
                onChange={(e) => setVendorPaymentForm({ ...vendorPaymentForm, utr_number: e.target.value })}
                placeholder="Enter UTR number"
                className="bg-secondary/50"
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setVendorUtrDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700">
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Confirm UTR
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Email P&L Report Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              Email P&L Report
            </DialogTitle>
            <DialogDescription>
              Send the current Profit & Loss report to an email address
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Email Address *</Label>
              <Input
                type="email"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                placeholder="Enter recipient email"
                className="bg-secondary/50"
                required
              />
            </div>
            <div className="p-3 bg-muted/30 rounded-lg text-sm">
              <p className="font-semibold mb-1">Report Period:</p>
              <p className="text-muted-foreground">{plDateFilter.startDate} to {plDateFilter.endDate}</p>
              <p className="font-semibold mt-2 mb-1">Summary:</p>
              <p className="text-green-400">Freight: ₹{profitLossData.totalFreight.toLocaleString()}</p>
              <p className="text-red-400">Vendor Cost: ₹{profitLossData.totalVendorCost.toLocaleString()}</p>
              <p className="text-orange-400">Expenses: ₹{profitLossData.expenses.toLocaleString()}</p>
              <p className={profitLossData.netProfit >= 0 ? 'text-emerald-400 font-bold' : 'text-red-400 font-bold'}>
                Net {profitLossData.netProfit >= 0 ? 'Profit' : 'Loss'}: ₹{Math.abs(profitLossData.netProfit).toLocaleString()}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={sendPLReportEmail} 
              disabled={sendingEmail || !emailAddress}
              className="bg-primary"
            >
              {sendingEmail ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Send Report
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* TDS Report Email Dialog */}
      <Dialog open={tdsEmailDialogOpen} onOpenChange={setTdsEmailDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Percent className="w-5 h-5 text-purple-400" />
              Email TDS Report
            </DialogTitle>
            <DialogDescription>
              Send the TDS report to an email address
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Email Address *</Label>
              <Input
                type="email"
                value={tdsEmailAddress}
                onChange={(e) => setTdsEmailAddress(e.target.value)}
                placeholder="Enter recipient email"
                className="bg-secondary/50"
                required
              />
            </div>
            <div className="p-3 bg-muted/30 rounded-lg text-sm">
              <p className="font-semibold mb-1">Report Period:</p>
              <p className="text-muted-foreground">{tdsDateFilter.startDate} to {tdsDateFilter.endDate}</p>
              <p className="font-semibold mt-2 mb-1">Summary:</p>
              <p className="text-green-400">TDS Receivable: {formatCurrency(tdsReportData.summary.total_tds_receivable)}</p>
              <p className="text-purple-400">TDS Payable: {formatCurrency(tdsReportData.summary.total_tds_payable)}</p>
              <p className={`font-bold ${tdsReportData.summary.net_tds >= 0 ? 'text-orange-400' : 'text-emerald-400'}`}>
                Net TDS {tdsReportData.summary.net_tds >= 0 ? 'Payable' : 'Receivable'}: {formatCurrency(Math.abs(tdsReportData.summary.net_tds))}
              </p>
              <p className="text-muted-foreground mt-2">Total Entries: {tdsReportData.total_entries}</p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setTdsEmailDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={sendTdsReportEmail} 
              disabled={sendingTdsEmail || !tdsEmailAddress}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {sendingTdsEmail ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Send TDS Report
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Transaction Dialog */}
      <Dialog open={editTransactionDialogOpen} onOpenChange={setEditTransactionDialogOpen}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary" />
              Edit Transaction
            </DialogTitle>
            <DialogDescription>
              Update transaction details. Transaction #{editingTransaction?.transaction_number}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditTransactionSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type *</Label>
                <Select 
                  value={editTransactionForm.transaction_type} 
                  onValueChange={(v) => setEditTransactionForm({...editTransactionForm, transaction_type: v})}
                >
                  <SelectTrigger className="bg-secondary/50">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="receipt">Receipt (Money In)</SelectItem>
                    <SelectItem value="payment">Payment (Money Out)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select 
                  value={editTransactionForm.category} 
                  onValueChange={(v) => setEditTransactionForm({...editTransactionForm, category: v})}
                >
                  <SelectTrigger className="bg-secondary/50">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client_advance">Client Advance</SelectItem>
                    <SelectItem value="client_balance">Client Balance</SelectItem>
                    <SelectItem value="vendor_advance">Vendor Advance</SelectItem>
                    <SelectItem value="vendor_balance">Vendor Balance</SelectItem>
                    <SelectItem value="expense_salary">Staff Salary</SelectItem>
                    <SelectItem value="expense_stationery">Stationery</SelectItem>
                    <SelectItem value="expense_office">Office Expense</SelectItem>
                    <SelectItem value="expense_fuel">Fuel Expense</SelectItem>
                    <SelectItem value="expense_maintenance">Maintenance</SelectItem>
                    <SelectItem value="expense_other">Other Expense</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount *</Label>
                <Input
                  type="number"
                  value={editTransactionForm.amount}
                  onChange={(e) => setEditTransactionForm({...editTransactionForm, amount: e.target.value})}
                  placeholder="0.00"
                  className="bg-secondary/50"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={editTransactionForm.transaction_date}
                  onChange={(e) => setEditTransactionForm({...editTransactionForm, transaction_date: e.target.value})}
                  className="bg-secondary/50"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Vehicle Number</Label>
                <Input
                  value={editTransactionForm.vehicle_number}
                  onChange={(e) => setEditTransactionForm({...editTransactionForm, vehicle_number: e.target.value.toUpperCase()})}
                  placeholder="MH12AB1234"
                  className="bg-secondary/50"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Bank Account</Label>
                <Select 
                  value={editTransactionForm.bank_account_id || "none"} 
                  onValueChange={(v) => setEditTransactionForm({...editTransactionForm, bank_account_id: v === "none" ? "" : v})}
                >
                  <SelectTrigger className="bg-secondary/50">
                    <SelectValue placeholder="Select account" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {bankAccounts.map((bank) => (
                      <SelectItem key={bank.id} value={bank.id}>{bank.account_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Reference Type</Label>
                <Select 
                  value={editTransactionForm.reference_type || "none"} 
                  onValueChange={(v) => setEditTransactionForm({...editTransactionForm, reference_type: v === "none" ? "" : v, reference_id: ""})}
                >
                  <SelectTrigger className="bg-secondary/50">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="vendor">Vendor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Reference</Label>
                <Select 
                  value={editTransactionForm.reference_id || "none"} 
                  onValueChange={(v) => setEditTransactionForm({...editTransactionForm, reference_id: v === "none" ? "" : v})}
                  disabled={!editTransactionForm.reference_type}
                >
                  <SelectTrigger className="bg-secondary/50">
                    <SelectValue placeholder="Select reference" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {editTransactionForm.reference_type === "client" && clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                    ))}
                    {editTransactionForm.reference_type === "vendor" && vendors.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Payment Mode</Label>
              <Select 
                value={editTransactionForm.payment_mode || "none"} 
                onValueChange={(v) => setEditTransactionForm({...editTransactionForm, payment_mode: v === "none" ? "" : v})}
              >
                <SelectTrigger className="bg-secondary/50">
                  <SelectValue placeholder="Select mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={editTransactionForm.description}
                onChange={(e) => setEditTransactionForm({...editTransactionForm, description: e.target.value})}
                placeholder="Transaction details"
                className="bg-secondary/50"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditTransactionDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-primary">
                <Save className="w-4 h-4 mr-2" />
                Update Transaction
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Mark Transfer Complete Dialog */}
      <Dialog open={markTransferDialogOpen} onOpenChange={setMarkTransferDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
              Mark Transfer Complete
            </DialogTitle>
            <DialogDescription>
              Record the UTR/Reference number after completing transfer in UCO Bank
            </DialogDescription>
          </DialogHeader>
          
          {selectedTransfer && (
            <div className="space-y-4">
              {/* Transfer Summary */}
              <div className="p-4 bg-secondary/30 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Vendor:</span>
                  <span className="font-medium">{selectedTransfer.vendor_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Account:</span>
                  <span className="font-mono text-sm">{selectedTransfer.account_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">IFSC:</span>
                  <span className="font-mono text-sm">{selectedTransfer.ifsc_code}</span>
                </div>
                <div className="flex justify-between border-t border-border pt-2 mt-2">
                  <span className="text-muted-foreground">Amount:</span>
                  <span className="font-bold text-lg text-primary">{formatCurrency(selectedTransfer.outstanding)}</span>
                </div>
              </div>

              {/* UTR Input */}
              <div className="space-y-2">
                <Label>UTR / Transaction Reference Number *</Label>
                <Input
                  value={transferForm.utr_number}
                  onChange={(e) => setTransferForm({ ...transferForm, utr_number: e.target.value })}
                  placeholder="Enter UCO Bank UTR number"
                  className="bg-secondary/50 font-mono"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  You can find this in UCO Bank transaction receipt or SMS
                </p>
              </div>

              {/* Transfer Date */}
              <div className="space-y-2">
                <Label>Transfer Date</Label>
                <Input
                  type="date"
                  value={transferForm.transfer_date}
                  onChange={(e) => setTransferForm({ ...transferForm, transfer_date: e.target.value })}
                  className="bg-secondary/50"
                />
              </div>

              {/* Remarks */}
              <div className="space-y-2">
                <Label>Remarks (Optional)</Label>
                <Input
                  value={transferForm.remarks}
                  onChange={(e) => setTransferForm({ ...transferForm, remarks: e.target.value })}
                  placeholder="UCO Bank NEFT/RTGS"
                  className="bg-secondary/50"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setMarkTransferDialogOpen(false);
              setSelectedTransfer(null);
              setTransferForm({ utr_number: "", transfer_date: new Date().toISOString().slice(0, 10), remarks: "" });
            }}>
              Cancel
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={markTransferCompleted}
              disabled={!transferForm.utr_number}
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Confirm Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountingPage;
